/**
 * React Query bindings for every endpoint.
 *
 * One hook per route, named after the route. Pages never call `api.get`
 * directly — that keeps cache keys consistent, which is what makes
 * invalidation work, which is what stops a stale position list being shown
 * after a stop was moved.
 *
 * Conventions:
 *
 * * `qk` is the single source of query keys. Ad-hoc key arrays are how two
 *   places end up caching the same data under different keys.
 * * Live-safety data (positions, kill-switch, health, alerts) polls on a
 *   short interval *and* is invalidated by websocket events. Belt and
 *   braces, on purpose: the poll is what keeps the screen honest if the
 *   socket has silently died.
 * * Long work (backtest, sweep, validation, download) returns a `JobOut`
 *   with 202. `useJobProgress` follows it over the websocket and falls back
 *   to polling, so a dropped socket degrades to a slower progress bar
 *   rather than a progress bar that stops at 4%.
 * * Mutations that change safety state invalidate aggressively. An
 *   over-fetch costs a few kilobytes; a stale "protected" badge costs money.
 */

import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import {
  AggregatedBarsError,
  verifyBarSpacing,
  MAX_COUNT_BACK,
  type BarWindowRequest,
  type BarWindowResponse,
  type ChartDataFeed,
} from '@/components/chart/dataFeed';

import { startLiveRefresh, type LiveRefreshPlan } from '@/lib/liveRefresh';

import { ApiError, api, type QueryParams } from './client';
import {
  bookOverviewShape,
  coinSideRowsShape,
  deploymentRowsShape,
  driftReportShape,
  engineCityTelemetryShape,
  healthShape,
  killSwitchShape,
  monitoringOverviewShape,
  reconciliationShape,
  safetyVerdictShape,
  unacknowledgedCountShape,
  type ShapeGuard,
} from './shapes';
import { TIMEFRAMES, TIMEFRAME_SECONDS, WS_TOPIC_POSITIONS } from './types';
import { subscribeCandles, subscribeJobs, ws, type ConnectionState } from './ws';
import type {
  AlertConfigOut,
  BookEnvironment,
  BookEquity,
  BookExcursion,
  BookGranularity,
  BookOverview,
  BookPeriods,
  BookTrades,
  MarketDataStatus,
  AlertOut,
  AlertQuery,
  AlertSeverity,
  AttributionDimension,
  AuditEntryOut,
  AuditQuery,
  BacktestChartPayload,
  BacktestCompareResponse,
  BacktestRequest,
  BacktestRunDetail,
  BacktestRunFacets,
  BacktestRunQuery,
  BacktestRunSummary,
  BalanceOut,
  BuiltinStrategyOut,
  CandleBar,
  CandleColumns,
  CandleQuery,
  CandleSeries,
  AttributionReport,
  CorrelationReport,
  DailyPnLOut,
  DataQualityOut,
  DataQualityOverviewRow,
  DbRowDeletedOut,
  DbRowDetailOut,
  DbRowPageOut,
  DbRowQuery,
  DbTableListOut,
  DeploymentActionRequest,
  DeploymentCreateRequest,
  DeployContractPreflightOut,
  DeploymentBasketCreateRequest,
  DeploymentBasketOut,
  DeploymentOut,
  DeploymentStatusOut,
  ExecutorStatusOut,
  EngineCityTelemetryOut,
  EngineCitySafetyOut,
  EngineCitySummaryOut,
  EngineCityEvidenceOut,
  EngineCityAccountsOut,
  EngineCityIncidentsOut,
  DownloadRequest,
  DrawingChartRef,
  DrawingIn,
  DrawingOut,
  DriftReportOut,
  EnvironmentOut,
  EpochSeconds,
  EquityPointOut,
  ExcursionReport,
  HealthOut,
  IndicatorCatalog,
  IndicatorComputeRequest,
  IndicatorResponse,
  IndicatorSpec,
  JobOut,
  JsonObject,
  KillSwitchOut,
  LeverageLimitsOut,
  LifecycleCapabilities,
  LifecycleEventOut,
  LifecycleOverrideRequest,
  LifecycleRegisterRequest,
  LifecycleStage,
  LifecycleVersionOut,
  LogLineOut,
  ManualHaltRequest,
  MonitoringCoinSide,
  MonitoringDeploymentRow,
  MonitoringDeploymentDetail,
  MonitoringOverview,
  MonitoringGroupDetail,
  GroupDecisionsOut,
  MonitoringLimit,
  MonitoringTrade,
  ExchangeExecutionsOut,
  OptimiseRequest,
  OptimiseResponse,
  Page,
  PeriodGranularity,
  PricesOut,
  PerformanceReport,
  PerformanceStatsOut,
  PortfolioBookingOut,
  PortfolioSummary,
  ConsolidatedPositionsOut,
  DeploymentRecordWrite,
  PositionOrdersOut,
  PreflightRequest,
  PreflightOut,
  PromotionRequest,
  RearmRequest,
  RuleSpecValidation,
  RunPeriodsOut,
  ScorecardOut,
  StrategyCreateRequest,
  StrategyDetailOut,
  StrategyQuery,
  StrategyRegistryOut,
  StrategySummaryOut,
  SymbolDetailOut,
  SymbolQuery,
  SymbolSummaryOut,
  Timeframe,
  TradeOut,
  TradeQuery,
  TradingCommandAcceptedOut,
  TradingCommandRequest,
  UnacknowledgedCountOut,
  UptimeOut,
  VersionOut,
} from './types';
import type { ReconciliationOut as ControlReconciliationOut } from '@/components/control/types';

// ══════════════════════════════════════════════════════════════════════
//  Query keys
// ══════════════════════════════════════════════════════════════════════

export const qk = {
  system: {
    all: ['system'] as const,
    health: () => ['system', 'health'] as const,
    version: () => ['system', 'version'] as const,
    environment: () => ['system', 'environment'] as const,
    killSwitch: () => ['system', 'killswitch'] as const,
    killSwitchHistory: (limit?: number) => ['system', 'killswitch', 'history', limit] as const,
    dataQuality: (symbol: string, timeframe: Timeframe, start?: string, end?: string) =>
      ['system', 'data-quality', symbol, timeframe, start, end] as const,
    dataQualityOverview: () => ['system', 'data-quality', 'overview'] as const,
    audit: (query?: AuditQuery) => ['system', 'audit', query ?? {}] as const,
    jobs: (kind?: string, limit?: number) => ['system', 'jobs', kind, limit] as const,
    uptime: () => ['system', 'uptime'] as const,
  },
  market: {
    all: ['market'] as const,
    symbols: (query?: SymbolQuery) => ['market', 'symbols', query ?? {}] as const,
    symbol: (symbol: string) => ['market', 'symbols', symbol] as const,
    /**
     * No parameters, deliberately. One upstream call returns every pair, so
     * every consumer shares one cache entry and one poll — keying by the
     * symbols requested would multiply both for an identical answer.
     */
    prices: () => ['market', 'prices'] as const,
    candles: (query: CandleQuery) => ['market', 'candles', query] as const,
    indicatorCatalog: (category?: string, includePatterns?: boolean) =>
      ['market', 'indicators', 'catalog', category, includePatterns] as const,
    indicatorSpec: (key: string) => ['market', 'indicators', 'catalog', key] as const,
    indicators: (request: IndicatorComputeRequest) =>
      ['market', 'indicators', request] as const,
    downloads: (limit?: number) => ['market', 'downloads', limit] as const,
    download: (jobId: string) => ['market', 'download', jobId] as const,
  },
  backtest: {
    all: ['backtest'] as const,
    runs: (query?: BacktestRunQuery) => ['backtest', 'runs', query ?? {}] as const,
    runFacets: () => ['backtest', 'runs', 'facets'] as const,
    run: (runId: number) => ['backtest', 'runs', runId] as const,
    chart: (runId: number) => ['backtest', 'runs', runId, 'chart'] as const,
    chartEvents: (runId: number) => ['backtest', 'runs', runId, 'chart', 'events'] as const,
    periods: (runId: number, granularity: PeriodGranularity) =>
      ['backtest', 'runs', runId, 'periods', granularity] as const,
    compare: (runIds: number[]) => ['backtest', 'compare', [...runIds].sort()] as const,
    job: (jobId: string) => ['backtest', 'job', jobId] as const,
    optimise: (jobId: string) => ['backtest', 'optimise', jobId] as const,
    leverageLimits: (symbol: string) => ['backtest', 'leverage-limits', symbol] as const,
  },
  strategies: {
    all: ['strategies'] as const,
    list: (query?: StrategyQuery) => ['strategies', 'list', query ?? {}] as const,
    detail: (id: number) => ['strategies', 'detail', id] as const,
    byName: (name: string) => ['strategies', 'by-name', name] as const,
    versions: (name: string) => ['strategies', 'by-name', name, 'versions'] as const,
    builtins: () => ['strategies', 'builtins'] as const,
    builtin: (name: string) => ['strategies', 'builtins', name] as const,
    /** §7-A per-version registry aggregate. */
    registry: () => ['strategies', 'registry'] as const,
  },
  lifecycle: {
    all: ['lifecycle'] as const,
    capabilities: () => ['lifecycle', 'capabilities'] as const,
    versions: (stage?: LifecycleStage, strategyName?: string) =>
      ['lifecycle', 'versions', stage, strategyName] as const,
    history: (id: number) => ['lifecycle', id, 'history'] as const,
    gates: (id: number, target?: LifecycleStage, runId?: number) =>
      ['lifecycle', id, 'gates', target, runId] as const,
    /** §7-E deploy preflight. Keyed by the sized book so a re-size re-runs it. */
    preflight: (id: number, target?: LifecycleStage, runId?: number, book?: JsonObject) =>
      ['lifecycle', id, 'preflight', target, runId, book ?? {}] as const,
  },
  portfolio: {
    all: ['portfolio'] as const,
    summary: () => ['portfolio', 'summary'] as const,
    /** §7-F consolidated per-(coin,side) block. */
    consolidated: () => ['portfolio', 'consolidated'] as const,
    /** §7-J reconciliation status. */
    reconciliation: () => ['portfolio', 'reconciliation'] as const,
    /** §7-K resting-order detail for one lot. */
    positionOrders: (positionId: string) => ['portfolio', 'positions', positionId, 'orders'] as const,
    balances: (deploymentId?: number) => ['portfolio', 'balances', deploymentId] as const,
    equity: (deploymentId: number, start?: string, end?: string, maxPoints?: number) =>
      ['portfolio', 'equity', deploymentId, start, end, maxPoints] as const,
    daily: (deploymentId: number, start?: string, end?: string) =>
      ['portfolio', 'daily', deploymentId, start, end] as const,
    trades: (query?: TradeQuery) => ['portfolio', 'trades', query ?? {}] as const,
    stats: (query?: JsonObject) => ['portfolio', 'stats', query ?? {}] as const,
    correlation: (query?: JsonObject) => ['portfolio', 'correlation', query ?? {}] as const,
    attribution: (query?: JsonObject) => ['portfolio', 'attribution', query ?? {}] as const,
    performance: (deploymentId: number, start?: string, end?: string) =>
      ['portfolio', 'performance', deploymentId, start, end] as const,
    excursion: (query?: JsonObject) => ['portfolio', 'excursion', query ?? {}] as const,
    bookings: (deploymentId?: number) => ['portfolio', 'bookings', deploymentId] as const,
  },
  commands: {
    all: ['commands'] as const,
    executor: () => ['commands', 'executor-status'] as const,
  },
  deployments: {
    all: ['deployments'] as const,
    list: (query?: JsonObject) => ['deployments', 'list', query ?? {}] as const,
    detail: (id: number) => ['deployments', id] as const,
    status: (id: number) => ['deployments', id, 'status'] as const,
    logs: (id: number, limit?: number) => ['deployments', id, 'logs', limit] as const,
    drift: (id: number) => ['deployments', id, 'drift'] as const,
    record: (id: number) => ['deployments', id, 'record'] as const,
  },
  monitoring: {
    all: ['monitoring-workspace'] as const,
    overview: () => ['monitoring-workspace', 'overview'] as const,
    deployments: () => ['monitoring-workspace', 'deployments'] as const,
    trades: (deploymentId?: number) =>
      ['monitoring-workspace', 'trades', deploymentId ?? 'all'] as const,
    executions: (deploymentId?: number) => ['monitoring-workspace', 'executions', deploymentId ?? 'all'] as const,
    coinSide: () => ['monitoring-workspace', 'coin-side'] as const,
    limits: () => ['monitoring-workspace', 'limits'] as const,
    engineCity: () => ['monitoring-workspace', 'engine-city'] as const,
  },
  alerts: {
    all: ['alerts'] as const,
    list: (query?: AlertQuery) => ['alerts', 'list', query ?? {}] as const,
    detail: (id: number) => ['alerts', id] as const,
    unackCount: (minSeverity?: AlertSeverity) => ['alerts', 'unack-count', minSeverity] as const,
    undelivered: (limit?: number) => ['alerts', 'undelivered', limit] as const,
    config: () => ['alerts', 'config'] as const,
  },
  drawings: {
    all: ['drawings'] as const,
    charts: () => ['drawings', 'charts'] as const,
    /**
     * Keyed by symbol alone — the timeframe is deliberately absent.
     *
     * A drawing is anchored in (UTC timestamp, price) and is therefore
     * defined at every interval, so a timeframe change cannot change this
     * answer. Including it would refetch on every timeframe click and flip
     * `isLoading`, which is how the chart used to lose its overlays (M7).
     */
    forSymbol: (symbol: string) => ['drawings', symbol] as const,
  },
  database: {
    all: ['database'] as const,
    tables: () => ['database', 'tables'] as const,
    /**
     * The prefix every page of one table shares.
     *
     * A delete invalidates THIS, not `all` — the row count changed for one
     * table and for `tables()`, and nothing else in the app got staler.
     */
    rowsForTable: (table: string) => ['database', 'rows', table] as const,
    rows: (table: string, query: DbRowQuery) =>
      ['database', 'rows', table, query] as const,
    row: (table: string, pk: string) => ['database', 'row', table, pk] as const,
  },
} as const;

/** Refetch cadences. Named so the intent is visible at the call site. */
export const POLL = {
  /** Liveness fallback; mark changes normally arrive through the positions WS. */
  liveMoney: 5_000,
  /** Anything that answers "am I exposed right now". */
  safety: 5_000,
  /** Deployment and job state. */
  operational: 10_000,
  /** Health and feeds. */
  ambient: 30_000,
} as const;

type QueryOpts<T> = Omit<UseQueryOptions<T, ApiError, T, readonly unknown[]>, 'queryKey' | 'queryFn'>;
type MutationOpts<TData, TVars> = Omit<
  UseMutationOptions<TData, ApiError, TVars>,
  'mutationFn'
>;

function asParams(value: object | undefined): QueryParams | undefined {
  return value as QueryParams | undefined;
}

/**
 * Refuse a 200 answer that is not in the shape a safety reading depends on [S4].
 *
 * Without this a malformed body became typed data: `reduce` over `{}` crashed
 * the whole root (there is no ErrorBoundary) and `'1'` concatenated instead of
 * adding. The rejection is an `ApiError` with `code: 'bad_response'` and status
 * 200, which `isRetryable` reports as not retryable, so it surfaces at once;
 * React Query keeps the last good data and reports `isError`, and every
 * consumer fails closed through its error path. The guards live in `shapes.ts`.
 */
function expectShape<T>(path: string, guard: ShapeGuard): (data: unknown) => T {
  return (data) => {
    const why = guard(data);
    if (why !== null) {
      throw new ApiError({
        code: 'bad_response',
        message: `the backend returned ${path} in an unexpected shape: ${why}`,
        status: 200,
      });
    }
    return data as T;
  };
}

/**
 * Read an endpoint that may not exist yet, and return `null` on a 404 instead
 * of throwing.
 *
 * The strategies-redesign panels (spec §7) are wired to backend surfaces that
 * arrive with the futures executor. Until then those endpoints 404, and a 404
 * here is not an error to shout about — it is "this capability is not built".
 * Returning `null` lets the panel render an explicit "awaiting futures
 * executor" pending state and light up unchanged when the route lands. Every
 * OTHER failure (500, transport, a real error body) still throws, so a genuine
 * outage on a route that DOES exist is not silently hidden as "pending".
 *
 * `data === null` therefore means "endpoint not present yet"; `data ===
 * undefined` (react-query's pre-fetch value) means "still loading".
 */
async function getOrNull<T>(
  path: string,
  options?: { query?: QueryParams; timeoutMs?: number },
): Promise<T | null> {
  try {
    return await api.get<T>(path, options ?? {});
  } catch (error) {
    // "Endpoint not present as designed yet" → null (render the pending state /
    // use the client-join fallback), NOT an error. Three shapes mean that:
    //   404 / not_found — no route at this path;
    //   405 — a route exists for another method only;
    //   422 — the path fell through to a sibling `/{id}` route and failed its
    //         validation (e.g. `/strategies/registry` before the dedicated
    //         aggregate endpoint exists). A real failure still surfaces.
    if (
      error instanceof ApiError &&
      (error.status === 404 ||
        error.status === 405 ||
        error.status === 422 ||
        error.code === 'not_found')
    ) {
      return null;
    }
    throw error;
  }
}

// ══════════════════════════════════════════════════════════════════════
//  System
// ══════════════════════════════════════════════════════════════════════

export function useHealth(options?: QueryOpts<HealthOut>): UseQueryResult<HealthOut, ApiError> {
  return useQuery({
    queryKey: qk.system.health(),
    queryFn: () => api.get<unknown>('/system/health').then(expectShape<HealthOut>('/system/health', healthShape)),
    refetchInterval: POLL.ambient,
    ...options,
  });
}

export function useVersion(options?: QueryOpts<VersionOut>): UseQueryResult<VersionOut, ApiError> {
  return useQuery({
    queryKey: qk.system.version(),
    queryFn: () => api.get<VersionOut>('/system/version'),
    staleTime: Infinity,
    ...options,
  });
}

/**
 * Which world we are in.
 *
 * Never `staleTime: Infinity`: this is what drives the LIVE banner, and a
 * cached "paper" over a live process is the single worst thing this UI
 * could display.
 */
export function useEnvironment(
  options?: QueryOpts<EnvironmentOut>,
): UseQueryResult<EnvironmentOut, ApiError> {
  return useQuery({
    queryKey: qk.system.environment(),
    queryFn: () => api.get<EnvironmentOut>('/system/environment'),
    refetchInterval: POLL.ambient,
    staleTime: 10_000,
    ...options,
  });
}

export function useKillSwitch(
  options?: QueryOpts<KillSwitchOut>,
): UseQueryResult<KillSwitchOut, ApiError> {
  return useQuery({
    queryKey: qk.system.killSwitch(),
    queryFn: () =>
      api.get<unknown>('/system/killswitch').then(expectShape<KillSwitchOut>('/system/killswitch', killSwitchShape)),
    refetchInterval: POLL.safety,
    // A mount or a return to the tab refetches at once: the 15 s default would
    // show a cached "armed" as current before the first answer.
    staleTime: 0,
    ...options,
  });
}

export function useKillSwitchHistory(
  limit = 50,
  options?: QueryOpts<AuditEntryOut[]>,
): UseQueryResult<AuditEntryOut[], ApiError> {
  return useQuery({
    queryKey: qk.system.killSwitchHistory(limit),
    queryFn: () => api.get<AuditEntryOut[]>('/system/killswitch/history', { query: { limit } }),
    ...options,
  });
}

/** Halt everything. Invalidates the whole cache — after a halt, nothing
 *  already on screen is trustworthy. */
export function useManualHalt(
  options?: MutationOpts<JsonObject, ManualHaltRequest>,
): UseMutationResult<JsonObject, ApiError, ManualHaltRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<JsonObject>('/system/killswitch/halt', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries();
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useRearmKillSwitch(
  options?: MutationOpts<KillSwitchOut, RearmRequest>,
): UseMutationResult<KillSwitchOut, ApiError, RearmRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<KillSwitchOut>('/system/killswitch/rearm', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.system.all });
      void client.invalidateQueries({ queryKey: qk.deployments.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useDataQuality(
  symbol: string,
  timeframe: Timeframe,
  range?: { start?: string; end?: string },
  options?: QueryOpts<DataQualityOut>,
): UseQueryResult<DataQualityOut, ApiError> {
  return useQuery({
    queryKey: qk.system.dataQuality(symbol, timeframe, range?.start, range?.end),
    queryFn: () =>
      api.get<DataQualityOut>('/system/data-quality', {
        query: { symbol, timeframe, start: range?.start, end: range?.end },
      }),
    enabled: symbol.length > 0,
    ...options,
  });
}

export function useDataQualityOverview(
  options?: QueryOpts<DataQualityOverviewRow[]>,
): UseQueryResult<DataQualityOverviewRow[], ApiError> {
  return useQuery({
    queryKey: qk.system.dataQualityOverview(),
    queryFn: () => api.get<DataQualityOverviewRow[]>('/system/data-quality/overview'),
    ...options,
  });
}

export function useAuditLog(
  query?: AuditQuery,
  options?: QueryOpts<AuditEntryOut[]>,
): UseQueryResult<AuditEntryOut[], ApiError> {
  return useQuery({
    queryKey: qk.system.audit(query),
    queryFn: () => api.get<AuditEntryOut[]>('/system/audit', { query: asParams(query) }),
    ...options,
  });
}

export function useJobs(
  kind?: string,
  limit = 50,
  options?: QueryOpts<JobOut[]>,
): UseQueryResult<JobOut[], ApiError> {
  return useQuery({
    queryKey: qk.system.jobs(kind, limit),
    queryFn: () => api.get<JobOut[]>('/system/jobs', { query: { kind, limit } }),
    refetchInterval: POLL.operational,
    ...options,
  });
}

export function useCancelJob(
  options?: MutationOpts<JobOut, string>,
): UseMutationResult<JobOut, ApiError, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (jobId) => api.post<JobOut>(`/system/jobs/${encodeURIComponent(jobId)}/cancel`),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: ['system', 'jobs'] });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useUptime(options?: QueryOpts<UptimeOut>): UseQueryResult<UptimeOut, ApiError> {
  return useQuery({
    queryKey: qk.system.uptime(),
    queryFn: () => api.get<UptimeOut>('/system/uptime'),
    refetchInterval: POLL.ambient,
    ...options,
  });
}

// ══════════════════════════════════════════════════════════════════════
//  Market data
// ══════════════════════════════════════════════════════════════════════

export function useSymbols(
  query?: SymbolQuery,
  options?: QueryOpts<Page<SymbolSummaryOut>>,
): UseQueryResult<Page<SymbolSummaryOut>, ApiError> {
  return useQuery({
    queryKey: qk.market.symbols(query),
    queryFn: () =>
      api.get<Page<SymbolSummaryOut>>('/market/symbols', {
        query: asParams(query),
        // `source=exchange|all` hits the venue; give it room before aborting.
        timeoutMs: query?.source && query.source !== 'stored' ? 30_000 : undefined,
      }),
    staleTime: 60_000,
    ...options,
  });
}

/**
 * Current price per pair, for the symbol list.
 *
 * One upstream call covers every pair, so this is deliberately NOT keyed by
 * the symbols asked for: a single unfiltered query, shared by every consumer,
 * costs the venue the same as one symbol and the cache the same as one entry.
 *
 * `refetchInterval` is deliberately LONGER than the server's snapshot TTL,
 * not equal to it. It used to be exactly equal (5,000 ms against a 5.0 s
 * TTL), and an interval measured from the end of the previous fetch meant the
 * snapshot was always older than the TTL when the next poll landed — so the
 * server's early-return could never fire. Measured over twelve minutes: 80
 * polls, 80 upstream round trips, a median of 3,272 ms for a 3.2 KB answer. A
 * cache whose TTL equals its caller's interval is not a cache.
 *
 * Polling faster than this would spend rate-limit budget an exit might need
 * (S3) and return the identical payload — the chart's websocket is what gives
 * the *selected* pair its tick-by-tick price, and that is the surface being
 * traded from. This feed is the authority for every OTHER pair, and for the
 * header price whenever the socket is not carrying one.
 */
export function usePrices(
  options?: QueryOpts<PricesOut>,
): UseQueryResult<PricesOut, ApiError> {
  return useQuery({
    queryKey: qk.market.prices(),
    queryFn: () => api.get<PricesOut>('/market/prices', { timeoutMs: 20_000 }),
    refetchInterval: 5_000,
    // A failed refresh must not blank the column: the previous prices stay on
    // screen and age out through the server's own `stale` flag, which is the
    // one authority on whether they may still be shown.
    placeholderData: keepPreviousData,
    staleTime: 4_000,
    ...options,
  });
}

export function useSymbol(
  symbol: string,
  options?: QueryOpts<SymbolDetailOut>,
): UseQueryResult<SymbolDetailOut, ApiError> {
  return useQuery({
    queryKey: qk.market.symbol(symbol),
    queryFn: () =>
      api.get<SymbolDetailOut>(`/market/symbols/${encodeURIComponent(symbol)}`, {
        timeoutMs: 30_000,
      }),
    enabled: symbol.length > 0,
    staleTime: 60_000,
    ...options,
  });
}

// ── Candles ───────────────────────────────────────────────────────────
//
// Rule M3 makes chart latency architectural, so the three things that cost
// a chart its responsiveness are all handled here rather than in whichever
// component happens to draw next.
//
// **The wire is columnar.** `format=columns` returns six parallel arrays
// instead of one object per bar. Measured on 3,000 BTCUSDT 1h bars: 385KB
// of JSON becomes 172KB, and 91KB becomes 53KB once gzip has been applied
// to both. `toSeries()` rebuilds the `bars` array once per fetch — inside
// the query function, so it happens on the network round trip and not on
// every render — and every consumer that reads `.bars` is unaffected.
//
// **History is immutable and the backend says so.** Every response carries
// a strong ETag. A window the caller closed in the past cannot acquire new
// bars, so it is cached forever within the session; only the window that
// runs to the live edge is ever revalidated, and revalidating it measured
// 1.2ms because the answer is a 304 with no body.
//
// **Reads are windowed.** `useCandleWindow` asks for the visible bars and
// nothing else, then fetches older pages as the user pans. Before this, a
// pan on BTCUSDT 1m measured 598ms because every request re-read eight
// years of parquet to return 3,000 bars.

/** Bars per page for the windowed reader. About four screens at normal zoom. */
const CANDLE_PAGE_SIZE = 1_500;

/** How many pages of history to keep in memory before dropping the oldest. */
const CANDLE_MAX_PAGES = 12;

/**
 * Rebuild the per-bar objects from the columns.
 *
 * Deliberately one pass with a pre-sized array: this runs on 1,500-3,000
 * bars per fetch and `push` into a growing array reallocates repeatedly.
 * The columns are kept alongside, not discarded — anything doing arithmetic
 * over a series wants the arrays.
 */
function toSeries(columns: CandleColumns): CandleSeries {
  const { t, o, h, l, c, v } = columns;
  const n = t.length;
  if (o.length !== n || h.length !== n || l.length !== n || c.length !== n || v.length !== n) {
    // Arrays of different lengths mean bar i's high belongs to bar j. A
    // chart drawn from that is not wrong in a way anyone would notice, which
    // is exactly why it must not be drawn at all.
    throw new Error(
      `candle columns disagree in length (t=${n} o=${o.length} h=${h.length} ` +
        `l=${l.length} c=${c.length} v=${v.length}) — refusing to draw misaligned bars`,
    );
  }
  const bars = t.map((time, i) => {
    const open = o[i];
    const high = h[i];
    const low = l[i];
    const close = c[i];
    const volume = v[i];
    if (
      open === undefined ||
      high === undefined ||
      low === undefined ||
      close === undefined ||
      volume === undefined
    ) {
      throw new Error(`candle column has a hole at index ${i}`);
    }
    return { t: time, open, high, low, close, volume };
  });
  return { ...columns, bars };
}

/** The query params for one candle request, with the columnar format forced. */
function candleParams(query: CandleQuery): CandleQuery {
  return { ...query, format: 'columns' };
}

function fetchCandles(query: CandleQuery): Promise<CandleSeries> {
  return api
    .get<CandleColumns>('/market/candles', {
      query: asParams(candleParams(query)),
      timeoutMs: 120_000,
    })
    .then(toSeries);
}

/**
 * The LIVE chart's history: recent closed candles from the futures feed
 * (testnet under `BHEEMA_ENV=testnet`), NOT the mainnet parquet store.
 *
 * `/market/live-candles` returns the same columnar shape as `/candles`, so
 * `toSeries` rebuilds `bars` identically — the difference is the venue. It is
 * `downsample_factor: 1` by contract and does not page: the window is one REST
 * page of the newest klines, and the `/ws` stream keeps the tail live. The
 * store path (`/market/candles`) is untouched and stays the Backtest tab's
 * deep, mainnet history.
 */
function fetchLiveCandles(
  symbol: string,
  timeframe: Timeframe,
  countback: number,
): Promise<CandleSeries> {
  return api
    .get<CandleColumns>('/market/live-candles', {
      query: { symbol, timeframe, countback },
      timeoutMs: 30_000,
    })
    .then(toSeries);
}

/**
 * How long a candle window stays fresh.
 *
 * A window with an explicit `end` is a closed piece of the past. It cannot
 * gain a bar, so refetching it is pure waste and it never goes stale. The
 * live window gains a bar every period, so it is revalidated — cheaply,
 * against the ETag.
 */
function candleStaleTime(query: CandleQuery): number {
  return query.end === undefined ? 15_000 : Infinity;
}

/**
 * Candles for one window.
 *
 * Returns `CandleSeries`: the columns, plus the `bars` array every chart
 * consumer already reads. Use `useCandleWindow` for a chart the user can
 * pan — this one fetches whatever range it is given, in one request.
 */
export function useCandles(
  query: CandleQuery,
  options?: QueryOpts<CandleSeries>,
): UseQueryResult<CandleSeries, ApiError> {
  return useQuery({
    queryKey: qk.market.candles(candleParams(query)),
    queryFn: () => fetchCandles(query),
    enabled: query.symbol.length > 0,
    staleTime: candleStaleTime(query),
    ...options,
  });
}

// ── Windowed / progressive loading ────────────────────────────────────

export interface CandleWindowOptions {
  /** Bars per request. Also the downsample target, so pages are never
   *  aggregates — every bar in a window is a real bar of that timeframe. */
  pageSize?: number;
  /** Memory ceiling. Older pages beyond this are not requested. */
  maxPages?: number;
  enabled?: boolean;
}

export interface CandleWindowResult {
  /** Every loaded page merged, oldest bar first. Null until the first lands. */
  series: CandleSeries | null;
  bars: CandleBar[];
  /** True only while the *first* page is in flight — the blank-chart state. */
  isLoading: boolean;
  /** True while older history is being fetched behind an already-drawn chart. */
  isFetchingOlder: boolean;
  /** True when the store holds bars older than the oldest one loaded. */
  hasOlder: boolean;
  /** Ask for the next page back. Safe to call repeatedly; it debounces itself. */
  loadOlder: () => void;
  /** True once `maxPages` is reached and no more will be requested. */
  atPageLimit: boolean;
  pagesLoaded: number;
  error: ApiError | null;
}

/**
 * Candles for a chart the user can pan, loaded a window at a time.
 *
 * The first page paints; older pages arrive when `loadOlder()` is called,
 * which the chart does as the viewport approaches the left edge of what it
 * holds. Pages are never aggregated — `max_points` is pinned to the page
 * size — so the merged series is real bars at uniform spacing and a chart
 * cannot end up drawing one resolution next to another.
 *
 * Pages are separate cache entries, which is the point: only the newest one
 * is ever revalidated, and every page behind it is closed history that this
 * session will not ask for twice.
 */
export function useCandleWindow(
  symbol: string,
  timeframe: Timeframe,
  options?: CandleWindowOptions,
): CandleWindowResult {
  const pageSize = options?.pageSize ?? CANDLE_PAGE_SIZE;
  const maxPages = options?.maxPages ?? CANDLE_MAX_PAGES;
  const enabled = (options?.enabled ?? true) && symbol.length > 0;

  // Each cursor is one page's exclusive `end`. `undefined` is the live edge.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);

  // A new chart is a new history. Keeping the old cursors would ask for
  // BTCUSDT's timestamps against ETHUSDT's series.
  useEffect(() => {
    setCursors([undefined]);
  }, [symbol, timeframe, pageSize]);

  const results = useQueries({
    queries: cursors.map((end) => {
      const query: CandleQuery = {
        symbol,
        timeframe,
        end,
        limit: pageSize,
        max_points: pageSize,
      };
      return {
        queryKey: qk.market.candles(candleParams(query)),
        queryFn: () => fetchCandles(query),
        enabled,
        staleTime: candleStaleTime(query),
      };
    }),
  });

  const pages = useMemo(
    () => results.map((r) => r.data).filter((d): d is CandleSeries => d !== undefined),
    [results],
  );

  // Cursors run newest-first; a chart reads oldest-first.
  const series = useMemo<CandleSeries | null>(() => {
    const ordered = [...pages].reverse();
    const oldest = ordered[0];
    const newest = ordered[ordered.length - 1];
    if (oldest === undefined || newest === undefined) return null;
    if (ordered.length === 1) return newest;
    return {
      ...newest,
      t: ordered.flatMap((p) => p.t),
      o: ordered.flatMap((p) => p.o),
      h: ordered.flatMap((p) => p.h),
      l: ordered.flatMap((p) => p.l),
      c: ordered.flatMap((p) => p.c),
      v: ordered.flatMap((p) => p.v),
      bars: ordered.flatMap((p) => p.bars),
      start: oldest.start,
      end: newest.end,
      returned: ordered.reduce((sum, p) => sum + p.returned, 0),
      source_bars: ordered.reduce((sum, p) => sum + p.source_bars, 0),
      // Only the oldest page knows whether anything is left behind it.
      truncated: oldest.truncated,
    };
  }, [pages]);

  const lastResult = results[results.length - 1];
  const oldestPage = pages.length === cursors.length ? pages[pages.length - 1] : undefined;
  const atPageLimit = cursors.length >= maxPages;
  const hasOlder = oldestPage?.truncated === true && !atPageLimit;

  const loadOlder = useCallback(() => {
    setCursors((current) => {
      if (current.length >= maxPages) return current;
      const tail = oldestPage;
      // Nothing to extend from until the current oldest page has landed, and
      // nothing to extend *to* once the store has no older bars.
      if (!tail || !tail.truncated) return current;
      const edge = tail.bars[0];
      if (edge === undefined) return current;
      const next = new Date(edge.t * 1_000).toISOString();
      if (current.includes(next)) return current;
      return [...current, next];
    });
  }, [maxPages, oldestPage]);

  const firstResult = results[0];
  return {
    series,
    bars: series?.bars ?? [],
    isLoading: firstResult?.isPending === true,
    isFetchingOlder: cursors.length > 1 && lastResult?.isFetching === true,
    hasOlder,
    loadOlder,
    atPageLimit,
    pagesLoaded: pages.length,
    error: (results.find((r) => r.error)?.error as ApiError | undefined) ?? null,
  };
}

// ── Prefetch ──────────────────────────────────────────────────────────

/**
 * Warm a candle window before anything asks for it.
 *
 * Cold and warm are two different products here: a first touch measured
 * 17-28ms and a repeat measured 1.5-2.2ms, because the backend keeps the
 * encoded response. Prefetching moves the cold read off the interaction and
 * into the idle time before it, which is the whole difference between a
 * timeframe button that responds and one that hesitates.
 */
function prefetchCandles(client: QueryClient, query: CandleQuery): void {
  void client.prefetchQuery({
    queryKey: qk.market.candles(candleParams(query)),
    queryFn: () => fetchCandles(query),
    staleTime: candleStaleTime(query),
  });
}

/**
 * Run `task` when the browser is idle, or very soon if it never is.
 *
 * Prefetching is by definition work nobody is waiting for, so it must never
 * be scheduled ahead of the render the user *is* waiting for. The `timeout`
 * is the floor: a tab that is continuously busy still warms its neighbours
 * within a second and a half. Guarded at runtime because Safari only got
 * `requestIdleCallback` recently, and a missing one must degrade to a short
 * timer rather than to no prefetch at all.
 */
function onIdle(task: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(() => task(), { timeout: 1_500 });
    return () => window.cancelIdleCallback?.(handle);
  }
  const timer = window.setTimeout(task, 250);
  return () => window.clearTimeout(timer);
}

/**
 * An imperative prefetch, for hover and focus handlers.
 *
 * The cheapest prefetch signal there is: the pointer reaches a timeframe
 * button a couple of hundred milliseconds before the click does, which is
 * longer than a cold read takes.
 */
export function useCandlePrefetch(): (query: CandleQuery) => void {
  const client = useQueryClient();
  return useCallback((query: CandleQuery) => {
    if (query.symbol.length === 0) return;
    prefetchCandles(client, query);
  }, [client]);
}

/**
 * Warm the timeframes either side of the one on screen.
 *
 * Timeframe switching is overwhelmingly a step to a neighbour — 1h to 4h,
 * 15m to 5m — so two steps in each direction covers nearly every switch for
 * four background requests. Runs on idle so it never competes with the
 * window the user is actually looking at.
 */
export function usePrefetchAdjacentTimeframes(
  symbol: string,
  timeframe: Timeframe,
  options?: { pageSize?: number; span?: number; enabled?: boolean },
): void {
  const client = useQueryClient();
  const pageSize = options?.pageSize ?? CANDLE_PAGE_SIZE;
  const span = options?.span ?? 2;
  const enabled = (options?.enabled ?? true) && symbol.length > 0;

  useEffect(() => {
    if (!enabled) return;
    const index = TIMEFRAMES.indexOf(timeframe);
    if (index < 0) return;
    const neighbours: Timeframe[] = [];
    for (let step = 1; step <= span; step += 1) {
      const before = TIMEFRAMES[index - step];
      const after = TIMEFRAMES[index + step];
      if (before !== undefined) neighbours.push(before);
      if (after !== undefined) neighbours.push(after);
    }
    return onIdle(() => {
      for (const neighbour of neighbours) {
        prefetchCandles(client, {
          symbol,
          timeframe: neighbour,
          limit: pageSize,
          max_points: pageSize,
        });
      }
    });
  }, [client, symbol, timeframe, span, pageSize, enabled]);
}

/**
 * Warm the symbols the owner has been moving between.
 *
 * Pass the most-recently-viewed few; the caller owns that list because only
 * it knows what the picker is showing. Bounded to eight so a long list
 * cannot turn a chart into fifty background requests.
 */
export function usePrefetchSymbols(
  symbols: readonly string[],
  timeframe: Timeframe,
  options?: { pageSize?: number; limit?: number; enabled?: boolean },
): void {
  const client = useQueryClient();
  const pageSize = options?.pageSize ?? CANDLE_PAGE_SIZE;
  const cap = options?.limit ?? 8;
  const enabled = options?.enabled ?? true;
  const key = symbols.join(',');

  useEffect(() => {
    if (!enabled || key.length === 0) return;
    const wanted = key.split(',').filter(Boolean).slice(0, cap);
    return onIdle(() => {
      for (const symbol of wanted) {
        prefetchCandles(client, {
          symbol,
          timeframe,
          limit: pageSize,
          max_points: pageSize,
        });
      }
    });
  }, [client, key, timeframe, pageSize, cap, enabled]);
}

/**
 * Warm the next page back, before the user pans into it.
 *
 * Call with the oldest bar currently held. A pan that needs older data then
 * finds it already cached instead of waiting on a read — measured at 31ms
 * for BTCUSDT 1m, which is the difference between a chart that follows the
 * cursor and one that catches up with it.
 */
export function usePrefetchOlderCandles(
  symbol: string,
  timeframe: Timeframe,
  oldestBarTime: number | null,
  options?: { pageSize?: number; enabled?: boolean },
): void {
  const client = useQueryClient();
  const pageSize = options?.pageSize ?? CANDLE_PAGE_SIZE;
  const enabled = (options?.enabled ?? true) && symbol.length > 0 && oldestBarTime !== null;

  useEffect(() => {
    if (!enabled || oldestBarTime === null) return;
    return onIdle(() => {
      prefetchCandles(client, {
        symbol,
        timeframe,
        end: new Date(oldestBarTime * 1_000).toISOString(),
        limit: pageSize,
        max_points: pageSize,
      });
    });
  }, [client, symbol, timeframe, oldestBarTime, pageSize, enabled]);
}

// ── The chart data feed ───────────────────────────────────────────────
//
// `useCandleWindow` above owns its own paging state, which suits a component
// that renders bars itself. A *chart* cannot use it: KLineChart decides when
// it needs older history — it knows where the viewport is, we do not — and
// asks through a callback that must be answered. So the chart gets a feed
// object instead of a hook result: "here is a window, give me its bars".
//
// Both go through the same `fetchCandles`, the same query keys and the same
// staleness rule, so a page one of them fetched is a cache hit for the other
// and for `usePrefetchOlderCandles`.

/**
 * How long a fetched page survives with nothing observing it.
 *
 * Longer than the app-wide 5 minutes on purpose: these entries are the
 * answer to "switching back to BTCUSDT 1h must be instant". A closed page of
 * history is immutable, so the only cost of keeping it is memory — 86 KB
 * per 1,500-bar page, measured.
 */
const CANDLE_PAGE_GC_MS = 30 * 60_000;

function isoOfSecond(seconds: number): string {
  return new Date(seconds * 1_000).toISOString();
}

/**
 * One page, through the cache.
 *
 * `fetchQuery` rather than `useQuery` because the caller is an event
 * (KLineChart asking for history), not a render. It still dedupes concurrent
 * identical requests and still honours `staleTime`, so a page already held
 * resolves without touching the network.
 */
function fetchCandlePage(client: QueryClient, query: CandleQuery): Promise<CandleSeries> {
  return client.fetchQuery({
    queryKey: qk.market.candles(candleParams(query)),
    queryFn: () => fetchCandles(query),
    staleTime: candleStaleTime(query),
    gcTime: CANDLE_PAGE_GC_MS,
  });
}

/**
 * Build the request for one window.
 *
 * `max_points` is pinned equal to `limit` on **every** path. They are
 * separate query parameters, the endpoint's own defaults are `limit=200000,
 * max_points=2000`, and that combination is the original defect: 200,000
 * hourly bars folded 38:1 into 38-hour candles served under a `1h` label,
 * HTTP 200, no warning. Pinning them is what makes `downsample_factor` 1;
 * the assertion below is what proves it happened.
 */
function candleWindowQuery(request: BarWindowRequest): CandleQuery {
  const { symbol, timeframe, direction, cursor, countBack } = request;
  // The endpoint's own bounds (limit 10..2,000,000, max_points 10..50,000).
  // Clamped rather than trusted: a caller that computed 0 from a zero-width
  // container would otherwise get a 422 and an empty chart.
  const limit = Math.min(MAX_COUNT_BACK, Math.max(10, Math.trunc(countBack)));
  const base: CandleQuery = { symbol, timeframe, limit, max_points: limit };

  if (cursor === null) return base;
  const interval = TIMEFRAME_SECONDS[timeframe] ?? 60;

  if (direction === 'older') {
    // `end` is EXCLUSIVE. Measured at all ten timeframes: page 2 requested
    // with end = page 1's oldest bar joins with a gap of exactly one
    // interval and zero duplicate timestamps, so no client-side dedup and
    // no off-by-one correction is needed here or anywhere downstream.
    return { ...base, end: isoOfSecond(cursor) };
  }

  // 'newer': the cursor is the newest bar we hold, so start one bar after
  // it. `end` is supplied too because the backend honours an explicit
  // `start` exactly and then keeps the *last* `limit` bars — without a
  // closing bound a request for the next 1,500 bars returns the newest
  // 1,500 instead, which would silently skip everything in between.
  const from = cursor + interval;
  return { ...base, start: isoOfSecond(from), end: isoOfSecond(from + interval * limit) };
}

export interface ChartDataFeedOptions {
  /** Follow the live bar over the websocket. Off for a static surface. */
  live?: boolean;
  /**
   * Where the chart's HISTORY comes from.
   *
   * - `'store'` (default): the mainnet parquet store via `/market/candles`,
   *   with full backward/forward paging. This is the deep-history path the
   *   Backtest tab and any archive view use.
   * - `'live'`: the futures feed via `/market/live-candles` — testnet under
   *   `BHEEMA_ENV=testnet` — a single recent window with no deep paging. The
   *   live edge is streamed over `/ws`, so the chart, its forming bar and the
   *   price all belong to ONE coherent venue.
   */
  source?: 'store' | 'live';
}

/**
 * The feed a pannable chart loads from.
 *
 * Two guarantees, both enforced here rather than hoped for:
 *
 * 1. **The server did not aggregate.** `downsample_factor` must be 1. This
 *    is checked from the response metadata before any bar is looked at,
 *    because it is the cheapest place to catch the exact bug that shipped.
 * 2. **The bars are one interval apart.** Checked bar by bar. A page that
 *    fails either check throws, and `PriceChart` renders an error instead of
 *    a chart — G5, applied to pixels.
 *
 * Returns a stable object, so installing it on a chart does not re-install
 * on every render (which in KLineChart means `resetData()`, which means a
 * full reload and a lost viewport).
 */
export function useChartDataFeed(options?: ChartDataFeedOptions): ChartDataFeed {
  const client = useQueryClient();
  const live = options?.live ?? true;
  const source = options?.source ?? 'store';

  return useMemo<ChartDataFeed>(
    () => ({
      fetchWindow: async (request): Promise<BarWindowResponse> => {
        // ── The live futures venue (testnet): one thin window, no paging ──
        //
        // The live chart's history is a single REST page of the newest
        // klines; the `/ws` stream keeps the tail live, so there is nothing to
        // page to. Only `initial` fetches; `older`/`newer` answer "no more" so
        // the chart stops asking rather than hammering a feed that does not
        // page. The spacing gate still runs — a composite bar must never reach
        // the chart, whatever venue it came from.
        if (source === 'live') {
          if (request.direction !== 'initial') {
            return { bars: [], hasOlder: false, hasNewer: false };
          }
          const countBack = Math.min(1_500, Math.max(10, Math.trunc(request.countBack)));
          const page = await fetchLiveCandles(request.symbol, request.timeframe, countBack);
          verifyBarSpacing(page.bars, request.timeframe);
          return { bars: page.bars, hasOlder: false, hasNewer: false };
        }

        // ── The mainnet store: deep history, fully pageable ──────────────
        const query = candleWindowQuery(request);
        const page = await fetchCandlePage(client, query);

        if (page.downsample_factor !== 1 || page.aggregated) {
          throw new AggregatedBarsError({
            symbol: request.symbol,
            timeframe: request.timeframe,
            factor: page.downsample_factor,
          });
        }
        verifyBarSpacing(page.bars, request.timeframe);

        return {
          bars: page.bars,
          // From the response, never from "the page came back full".
          // BTCUSDT 1w holds 449 bars in total, so its first page is short
          // AND has nothing behind it; a fullness heuristic would page
          // forever on long series and stop early on short ones.
          hasOlder: page.truncated,
          // `complete` means "this window runs to the newest stored bar",
          // which is only a question worth asking of a forward page. On an
          // initial or older page the newer bars are already on the chart.
          hasNewer: request.direction === 'newer' ? !page.complete : false,
        };
      },

      // The live tail is the `/ws` candle stream — the SAME venue as the
      // history above and as (once live) execution.
      //
      // The old bolted-on REST poll of `/market/candles` is gone: it read the
      // MAINNET store, so its "live" edge was a different venue from the
      // testnet chart and could never match the header price. Now the chart's
      // forming bar, the header ticker and the symbol-list price all read the
      // one `candles:SYMBOL:TIMEFRAME` stream, so the same number appears in
      // all three. `subscribeCandles` ref-counts the topic, so this and the
      // header's own subscription share one exchange stream.
      //
      // Fail CLOSED on a dead stream: an `error` frame (the upstream stream
      // stopped) is dropped, not drawn, and a bar is only fed while the socket
      // is not stale — the heartbeat is the authority, and a frozen bar must
      // never be presented as a live one. A frame arriving is itself proof of
      // freshness (it resets the staleness clock), so a real tick is never
      // withheld.
      subscribe: live
        ? ({ symbol, timeframe, onBar }) =>
            subscribeCandles(symbol, timeframe, (candle) => {
              if (candle.error !== undefined) return; // stream stopped — never draw it as live
              if (ws.getState().stale) return; // heartbeat says silent — fail closed
              const { t, open, high, low, close, volume } = candle;
              // A malformed frame must not poison the last bar's OHLC. The
              // chart draws exactly one bar per frame — same timestamp
              // replaces in place, newer appends — so one bad number is one
              // bad candle, and this refuses it entirely.
              if (
                ![t, open, high, low, close, volume].every((value) => Number.isFinite(value))
              ) {
                return;
              }
              onBar({ t, open, high, low, close, volume });
            })
        : undefined,
    }),
    [client, live, source],
  );
}

/**
 * The exact range and grid to ask `/market/indicators` for.
 *
 * Hand it the window `PriceChart` reports and pass the result straight into
 * the indicator request. The two calls **must** agree on `start`, `end` and
 * `max_points`: the server buckets by `max_points`, so a mismatch puts the
 * indicator points on a different grid from the candles and the chart draws
 * none of them — silently, because an indicator with no points is
 * indistinguishable from an indicator that has not landed yet.
 *
 * `end` is the newest bar's open time plus one interval, because the range
 * is half-open and the newest bar must be inside it.
 */
export function indicatorWindowFor(
  window: {
    timeframe: Timeframe;
    from: EpochSeconds;
    to: EpochSeconds;
    bars: readonly unknown[];
  } | null,
): { start: string; end: string; maxPoints: number } | undefined {
  if (window === null || window.bars.length === 0) return undefined;
  const interval = TIMEFRAME_SECONDS[window.timeframe] ?? 60;
  return {
    start: isoOfSecond(window.from),
    end: isoOfSecond(window.to + interval),
    // Equal to the bar count, so the endpoint has nothing to downsample.
    // Clamped to the endpoint's own bounds (10..50,000).
    maxPoints: Math.min(50_000, Math.max(10, window.bars.length)),
  };
}

export function useIndicatorCatalog(
  category?: string,
  includePatterns = false,
  options?: QueryOpts<IndicatorCatalog>,
): UseQueryResult<IndicatorCatalog, ApiError> {
  return useQuery({
    queryKey: qk.market.indicatorCatalog(category, includePatterns),
    queryFn: () =>
      api.get<IndicatorCatalog>('/market/indicators/catalog', {
        query: { category, include_patterns: includePatterns },
      }),
    staleTime: Infinity,
    ...options,
  });
}

export function useIndicatorSpec(
  key: string,
  options?: QueryOpts<IndicatorSpec>,
): UseQueryResult<IndicatorSpec, ApiError> {
  return useQuery({
    queryKey: qk.market.indicatorSpec(key),
    queryFn: () => api.get<IndicatorSpec>(`/market/indicators/catalog/${encodeURIComponent(key)}`),
    enabled: key.length > 0,
    staleTime: Infinity,
    ...options,
  });
}

/**
 * Indicator values, computed in Python.
 *
 * A POST that behaves like a query, so it is `useQuery` with the request
 * body in the key. This is the *only* source of indicator values in the
 * app: the chart registers custom indicators whose `calc()` returns these
 * pre-supplied numbers rather than computing anything in JavaScript.
 */
export function useIndicators(
  request: IndicatorComputeRequest | null,
  options?: QueryOpts<IndicatorResponse>,
): UseQueryResult<IndicatorResponse, ApiError> {
  const client = useQueryClient();
  useEffect(() => {
    if (request?.source !== 'live') return;
    // Match the chart's closed-bar frontier. The forming candle remains a
    // chart-only observation; indicators advance after authoritative closure.
    return subscribeCandles(request.symbol, request.timeframe, (candle) => {
      if (candle.closed && candle.error === undefined) {
        void client.invalidateQueries({ queryKey: qk.market.indicators(request), exact: true });
      }
    });
  }, [client, request]);
  return useQuery({
    queryKey: qk.market.indicators(request ?? ({} as IndicatorComputeRequest)),
    queryFn: async ({ signal }) => {
      const response = await api.post<IndicatorResponse>('/market/indicators', request, {
        signal,
        timeoutMs: request?.source === 'live' ? 10_000 : 120_000,
      });
      if (request?.source === 'live' && (response.source !== 'live' ||
          response.symbol !== request.symbol.toUpperCase() || response.timeframe !== request.timeframe)) {
        throw new ApiError({ code: 'indicator_source_mismatch', status: 502,
          message: 'Live indicator provenance does not match the selected chart.' });
      }
      return response;
    },
    enabled: request !== null && request.indicators.length > 0 && request.symbol.length > 0,
    staleTime: 30_000,
    refetchInterval: request?.source === 'live' ? 5_000 : false,
    // A rejected live observation invalidates the displayed overlay immediately.
    // Query retries otherwise retain successful data while hiding the failure.
    // The next five-second poll recovers it after an authoritative success [S4].
    ...(request?.source === 'live' ? { retry: false } : {}),
    ...options,
  });
}

export function useStartDownload(
  options?: MutationOpts<JobOut, DownloadRequest>,
): UseMutationResult<JobOut, ApiError, DownloadRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<JobOut>('/market/download', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: ['market', 'downloads'] });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useDownloadStatus(
  jobId: string | null,
  options?: QueryOpts<JobOut>,
): UseQueryResult<JobOut, ApiError> {
  return useQuery({
    queryKey: qk.market.download(jobId ?? ''),
    queryFn: () => api.get<JobOut>(`/market/download/${encodeURIComponent(jobId ?? '')}`),
    enabled: jobId !== null && jobId.length > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'running' || status === 'pending' || status === 'queued' ? 1_500 : false;
    },
    ...options,
  });
}

export function useDownloads(
  limit = 25,
  options?: QueryOpts<JobOut[]>,
): UseQueryResult<JobOut[], ApiError> {
  return useQuery({
    queryKey: qk.market.downloads(limit),
    queryFn: () => api.get<JobOut[]>('/market/downloads', { query: { limit } }),
    refetchInterval: POLL.operational,
    ...options,
  });
}

// ══════════════════════════════════════════════════════════════════════
//  Backtests
// ══════════════════════════════════════════════════════════════════════

/** Returns 202 + a job. Follow it with `useJobProgress`. */
export function useRunBacktest(
  options?: MutationOpts<JobOut, BacktestRequest>,
): UseMutationResult<JobOut, ApiError, BacktestRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<JobOut>('/backtest/run', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: ['backtest', 'runs'] });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

/** Blocking variant. Only for small ranges — it holds a connection open. */
export function useRunBacktestSync(
  options?: MutationOpts<JsonObject, BacktestRequest>,
): UseMutationResult<JsonObject, ApiError, BacktestRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<JsonObject>('/backtest/run-sync', body, { timeoutMs: 600_000 }),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: ['backtest', 'runs'] });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useBacktestJob(
  jobId: string | null,
  options?: QueryOpts<JsonObject>,
): UseQueryResult<JsonObject, ApiError> {
  return useQuery({
    queryKey: qk.backtest.job(jobId ?? ''),
    queryFn: () => api.get<JsonObject>(`/backtest/jobs/${encodeURIComponent(jobId ?? '')}`),
    enabled: jobId !== null && jobId.length > 0,
    ...options,
  });
}

/**
 * How much leverage this symbol's stops actually tolerate. [F1]
 *
 * Leverage does not change position size — it moves liquidation, which is a
 * second stop placed by the venue. A setup whose own stop sits outside
 * liquidation is refused by the strategy, so a high setting does not fail
 * loudly, it quietly returns fewer trades. This is what lets the leverage
 * row mark those settings before they are clicked.
 *
 * `max_safe_leverage` is null when there is no stored history for the symbol
 * to measure against — unknown, which the control renders as "nothing
 * flagged" rather than as "all safe".
 */
export function useLeverageLimits(
  symbol: string | null,
  options?: QueryOpts<LeverageLimitsOut>,
): UseQueryResult<LeverageLimitsOut, ApiError> {
  return useQuery({
    queryKey: qk.backtest.leverageLimits(symbol ?? ''),
    queryFn: () =>
      api.get<LeverageLimitsOut>('/backtest/leverage-limits', {
        query: { symbol: symbol ?? '' },
      }),
    enabled: typeof symbol === 'string' && symbol.length > 0,
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useBacktestRuns(
  query?: BacktestRunQuery,
  options?: QueryOpts<Page<BacktestRunSummary>>,
): UseQueryResult<Page<BacktestRunSummary>, ApiError> {
  return useQuery({
    queryKey: qk.backtest.runs(query),
    queryFn: () =>
      api.get<Page<BacktestRunSummary>>('/backtest/runs', { query: asParams(query) }),
    ...options,
  });
}

/**
 * Symbols and timeframes present across the whole registry.
 *
 * Fetched rather than derived from the loaded page: with the filters applied
 * server-side the page is a subset, and a dropdown built from it could not
 * offer a way back to the symbol that is currently filtered out. Cached for
 * five minutes — the set only changes when a run on a new instrument lands.
 */
export function useBacktestRunFacets(
  options?: QueryOpts<BacktestRunFacets>,
): UseQueryResult<BacktestRunFacets, ApiError> {
  return useQuery({
    queryKey: qk.backtest.runFacets(),
    queryFn: () => api.get<BacktestRunFacets>('/backtest/runs/facets'),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/**
 * Hide a run from the default registry view, or bring it back.
 *
 * There is no delete counterpart and there is not meant to be one: the run,
 * its trades and its equity curve stay on disk either way. Invalidates only
 * the run lists — the run's own detail is unchanged by archiving it, and
 * blowing that cache away would drop an open equity curve for nothing.
 */
export function useArchiveRun(
  options?: MutationOpts<BacktestRunSummary, { runId: number; archived: boolean }>,
): UseMutationResult<BacktestRunSummary, ApiError, { runId: number; archived: boolean }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, archived }) =>
      api.post<BacktestRunSummary>(`/backtest/runs/${runId}/archive`, { archived }),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.backtest.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

/*
 * ── Cache lifetime for one finished run ──────────────────────────────
 *
 * A completed run is immutable: its trades, metrics and curve were written
 * once and the row is never updated (archiving flips a flag on the SUMMARY,
 * and `useArchiveRun` deliberately leaves this cache alone). So the detail,
 * chart payload, stats and trade pages are held for an hour and refreshed in
 * the background at most every 30s — which is what makes returning to the
 * Results tab render instantly from cache instead of flashing a skeleton.
 *
 * A RUNNING run's detail does change; 30s of staleness on a screen whose
 * progress is streamed by the job endpoint is acceptable, and completion
 * invalidates `qk.backtest.all` via the launch mutation.
 */
const RUN_STALE_MS = 30_000;
const RUN_GC_MS = 60 * 60_000;

function fetchBacktestRun(runId: number): Promise<BacktestRunDetail> {
  return api.get<BacktestRunDetail>(`/backtest/runs/${runId}`, {
    query: { include_curve: false, include_trades: false },
    timeoutMs: 120_000,
  });
}

function fetchBacktestChart(runId: number): Promise<BacktestChartPayload> {
  return api.get<BacktestChartPayload>(`/backtest/runs/${runId}/chart`, {
    query: { include_trades: false, include_events: false },
    timeoutMs: 120_000,
  });
}

/**
 * The first trade page the Results screen requests for a run.
 *
 * One definition, shared by the screen and the prefetcher: the prefetch is
 * only a prefetch if its query key is byte-identical to the one the screen
 * will ask for a moment later.
 */
export function initialTradeQuery(runId: number, pageSize: number): TradeQuery {
  return {
    backtest_run_id: runId,
    outcome: 'all',
    sort: 'entry_time',
    order: 'desc',
    limit: pageSize,
    offset: 0,
  };
}

export function useBacktestRun(
  runId: number | null,
  options?: QueryOpts<BacktestRunDetail>,
): UseQueryResult<BacktestRunDetail, ApiError> {
  return useQuery({
    queryKey: qk.backtest.run(runId ?? -1),
    queryFn: () => fetchBacktestRun(runId ?? -1),
    enabled: runId !== null && runId > 0,
    staleTime: RUN_STALE_MS,
    gcTime: RUN_GC_MS,
    ...options,
  });
}

/**
 * Warm every query the Results screen fires for one run — detail, chart
 * payload, performance stats and the first trade page — so that opening the
 * run renders from cache instead of from four spinners.
 *
 * Used from the run registry on row hover and, staggered through idle time,
 * for the rows on screen. `prefetchQuery` respects `staleTime`, so a run
 * already warmed is a no-op rather than four duplicate requests.
 */
export function useBacktestRunPrefetch(pageSize: number): (runId: number) => void {
  const client = useQueryClient();
  return useCallback(
    (runId: number) => {
      if (!Number.isFinite(runId) || runId <= 0) return;
      void client.prefetchQuery({
        queryKey: qk.backtest.run(runId),
        queryFn: () => fetchBacktestRun(runId),
        staleTime: RUN_STALE_MS,
        gcTime: RUN_GC_MS,
      });
      void client.prefetchQuery({
        queryKey: qk.backtest.chart(runId),
        queryFn: () => fetchBacktestChart(runId),
        staleTime: RUN_STALE_MS,
        gcTime: RUN_GC_MS,
      });
      const stats: PerformanceStatsQuery = { backtest_run_id: runId };
      void client.prefetchQuery({
        queryKey: qk.portfolio.stats(stats as JsonObject),
        queryFn: () => api.get<PerformanceStatsOut>('/portfolio/stats', { query: asParams(stats) }),
        staleTime: RUN_STALE_MS,
        gcTime: RUN_GC_MS,
      });
      const trades = initialTradeQuery(runId, pageSize);
      void client.prefetchQuery({
        queryKey: qk.portfolio.trades(trades),
        queryFn: () => api.get<Page<TradeOut>>('/portfolio/trades', { query: asParams(trades) }),
        staleTime: RUN_STALE_MS,
        gcTime: RUN_GC_MS,
      });
    },
    [client, pageSize],
  );
}

/**
 * Warm one candle window (and optionally the indicator series computed over
 * it) before it is asked for on screen.
 *
 * This is what makes clicking a trade instant: the Results screen prefetches
 * the hovered row's window, so by the time the click lands the bars are
 * already in the cache. Keys are built with the same helpers the live hooks
 * use — a prefetch under a differently-shaped key is just a wasted request.
 */
export function useCandleWindowPrefetch(): (
  candles: CandleQuery,
  indicators?: IndicatorComputeRequest | null,
) => void {
  const client = useQueryClient();
  return useCallback(
    (candles: CandleQuery, indicators?: IndicatorComputeRequest | null) => {
      if (candles.symbol.length === 0) return;
      void client.prefetchQuery({
        queryKey: qk.market.candles(candleParams(candles)),
        queryFn: () => fetchCandles(candles),
        staleTime: candleStaleTime(candles),
        gcTime: CANDLE_PAGE_GC_MS,
      });
      if (indicators && indicators.indicators.length > 0 && indicators.symbol.length > 0) {
        void client.prefetchQuery({
          queryKey: qk.market.indicators(indicators),
          queryFn: () =>
            api.post<IndicatorResponse>('/market/indicators', indicators, {
              timeoutMs: 120_000,
            }),
          staleTime: RUN_GC_MS,
          gcTime: RUN_GC_MS,
        });
      }
    },
    [client],
  );
}

/**
 * Equity, benchmark, markers and the full event log for a run.
 *
 * `complete: false` means the event log was cached only in the process that
 * ran the backtest and was lost to a restart. Show that plainly — the curve
 * and trades are still real, but "why didn't it trade here" cannot be
 * answered without a re-run.
 */
export function useBacktestChart(
  runId: number | null,
  options?: QueryOpts<BacktestChartPayload>,
): UseQueryResult<BacktestChartPayload, ApiError> {
  return useQuery({
    queryKey: qk.backtest.chart(runId ?? -1),
    queryFn: () => fetchBacktestChart(runId ?? -1),
    enabled: runId !== null && runId > 0,
    staleTime: RUN_STALE_MS,
    gcTime: RUN_GC_MS,
    ...options,
  });
}

/** Decision events are potentially the largest object in a fresh run.
 * Fetch them only when the Decisions tab is actually opened. */
export function useBacktestEvents(
  runId: number | null,
  options?: QueryOpts<BacktestChartPayload>,
): UseQueryResult<BacktestChartPayload, ApiError> {
  return useQuery({
    queryKey: qk.backtest.chartEvents(runId ?? -1),
    queryFn: () =>
      api.get<BacktestChartPayload>(`/backtest/runs/${runId}/chart`, {
        query: { include_curve: false, include_trades: false, include_events: true },
        timeoutMs: 120_000,
      }),
    enabled: runId !== null && runId > 0,
    ...options,
  });
}

/*
 * ── The year-by-year (or month-by-month) slice of one run ────────────
 *
 * `basis` is pinned to `entry` here rather than exposed as a parameter:
 * the popup states one attribution rule and every number in it obeys that
 * rule. Offering both bases from the same table is how a trade straddling
 * a year boundary gets counted twice in a screenshot and zero times in an
 * argument.
 */
/**
 * The wire shape the backend actually sends — a FLAT summary, the period
 * fields at the top level beside `rows`. The component reads a nested
 * `summary` object, so the two are reconciled here at the boundary rather
 * than reshaping either side: the backend response is the tested, cent-exact
 * source of truth (test_run_periods.py), and the component's nested shape is
 * the more readable one to render from. Two agents built these in parallel
 * and never met in the middle; this adapter is that meeting.
 */
interface RunPeriodsWire {
  run_id: number;
  granularity: PeriodGranularity;
  basis: 'entry' | 'exit';
  rows: RunPeriodsOut['rows'];
  total_periods: number;
  green_periods: number;
  median_pnl: string | null;
  best_period: string | null;
  worst_period: string | null;
  total: RunPeriodsOut['total'];
}

function extreme(
  label: string | null,
  rows: RunPeriodsOut['rows'],
): RunPeriodsOut['summary']['best'] {
  if (label === null) return null;
  const row = rows.find((r) => r.period === label);
  // '0' when the extreme's row is not on the current page — the strip shows
  // the label; the exact pnl is a row in the table below it either way.
  return { period: label, pnl: row ? row.pnl : '0' };
}

async function fetchRunPeriods(
  runId: number,
  granularity: PeriodGranularity,
): Promise<RunPeriodsOut> {
  const wire = await api.get<RunPeriodsWire>(`/backtest/runs/${runId}/periods`, {
    query: { granularity, basis: 'entry' },
    timeoutMs: 120_000,
  });
  return {
    rows: wire.rows,
    summary: {
      median_pnl: wire.median_pnl,
      green: wire.green_periods,
      total: wire.total_periods,
      best: extreme(wire.best_period, wire.rows),
      worst: extreme(wire.worst_period, wire.rows),
      basis: wire.basis,
      granularity: wire.granularity,
    },
    total: wire.total ?? null,
  };
}

/**
 * Per-period P&L for one run, sliced by calendar bucket.
 *
 * `staleTime: Infinity` because the answer is immutable: a completed run's
 * trades were written once, so its 2023 P&L cannot change without the run id
 * changing too. Holding it for the session is what lets the granularity
 * control flick between Year and Month without a spinner on the second look.
 * `gcTime` still bounds memory — immutable is not the same as worth keeping
 * after the owner has moved on.
 */
export function useRunPeriods(
  runId: number | null,
  granularity: PeriodGranularity,
  options?: QueryOpts<RunPeriodsOut>,
): UseQueryResult<RunPeriodsOut, ApiError> {
  return useQuery({
    queryKey: qk.backtest.periods(runId ?? -1, granularity),
    queryFn: () => fetchRunPeriods(runId ?? -1, granularity),
    enabled: runId !== null && runId > 0,
    staleTime: Infinity,
    gcTime: RUN_GC_MS,
    ...options,
  });
}

/**
 * Warm one granularity of the period breakdown before the tile is clicked —
 * same pattern as `useBacktestRunPrefetch`: the key and `staleTime` are
 * byte-identical to `useRunPeriods`'s, so the click is a pure cache hit and
 * re-warming a settled query is a no-op rather than a duplicate request.
 */
export function useRunPeriodsPrefetch(): (
  runId: number,
  granularity: PeriodGranularity,
) => void {
  const client = useQueryClient();
  return useCallback(
    (runId: number, granularity: PeriodGranularity) => {
      if (!Number.isFinite(runId) || runId <= 0) return;
      void client.prefetchQuery({
        queryKey: qk.backtest.periods(runId, granularity),
        queryFn: () => fetchRunPeriods(runId, granularity),
        staleTime: Infinity,
        gcTime: RUN_GC_MS,
      });
    },
    [client],
  );
}

export function useCompareRuns(
  runIds: number[],
  options?: QueryOpts<BacktestCompareResponse>,
): UseQueryResult<BacktestCompareResponse, ApiError> {
  return useQuery({
    queryKey: qk.backtest.compare(runIds),
    queryFn: () =>
      api.get<BacktestCompareResponse>('/backtest/compare', { query: { run_ids: runIds } }),
    enabled: runIds.length >= 2,
    ...options,
  });
}

export function useOptimise(
  options?: MutationOpts<JobOut, OptimiseRequest>,
): UseMutationResult<JobOut, ApiError, OptimiseRequest> {
  return useMutation({
    mutationFn: (body) => api.post<JobOut>('/backtest/optimise', body),
    ...options,
  });
}

export function useOptimiseResult(
  jobId: string | null,
  options?: QueryOpts<OptimiseResponse>,
): UseQueryResult<OptimiseResponse, ApiError> {
  return useQuery({
    queryKey: qk.backtest.optimise(jobId ?? ''),
    queryFn: () =>
      api.get<OptimiseResponse>(`/backtest/optimise/${encodeURIComponent(jobId ?? '')}`, {
        timeoutMs: 120_000,
      }),
    enabled: jobId !== null && jobId.length > 0,
    retry: false,
    ...options,
  });
}

// ══════════════════════════════════════════════════════════════════════
//  Strategies
// ══════════════════════════════════════════════════════════════════════

export function useBuiltinStrategies(
  options?: QueryOpts<BuiltinStrategyOut[]>,
): UseQueryResult<BuiltinStrategyOut[], ApiError> {
  return useQuery({
    queryKey: qk.strategies.builtins(),
    queryFn: () => api.get<BuiltinStrategyOut[]>('/strategies/builtins'),
    staleTime: Infinity,
    ...options,
  });
}

export function useBuiltinStrategy(
  name: string,
  options?: QueryOpts<BuiltinStrategyOut>,
): UseQueryResult<BuiltinStrategyOut, ApiError> {
  return useQuery({
    queryKey: qk.strategies.builtin(name),
    queryFn: () => api.get<BuiltinStrategyOut>(`/strategies/builtins/${encodeURIComponent(name)}`),
    enabled: name.length > 0,
    staleTime: Infinity,
    ...options,
  });
}

export function useStrategies(
  query?: StrategyQuery,
  options?: QueryOpts<Page<StrategySummaryOut>>,
): UseQueryResult<Page<StrategySummaryOut>, ApiError> {
  return useQuery({
    queryKey: qk.strategies.list(query),
    queryFn: () => api.get<Page<StrategySummaryOut>>('/strategies', { query: asParams(query) }),
    ...options,
  });
}

export function useStrategy(
  strategyId: number | null,
  options?: QueryOpts<StrategyDetailOut>,
): UseQueryResult<StrategyDetailOut, ApiError> {
  return useQuery({
    queryKey: qk.strategies.detail(strategyId ?? -1),
    queryFn: () => api.get<StrategyDetailOut>(`/strategies/${strategyId}`),
    enabled: strategyId !== null && strategyId > 0,
    ...options,
  });
}

export function useStrategyByName(
  name: string,
  options?: QueryOpts<StrategyDetailOut>,
): UseQueryResult<StrategyDetailOut, ApiError> {
  return useQuery({
    queryKey: qk.strategies.byName(name),
    queryFn: () => api.get<StrategyDetailOut>(`/strategies/by-name/${encodeURIComponent(name)}`),
    enabled: name.length > 0,
    ...options,
  });
}

export function useStrategyVersions(
  name: string,
  options?: QueryOpts<StrategySummaryOut[]>,
): UseQueryResult<StrategySummaryOut[], ApiError> {
  return useQuery({
    queryKey: qk.strategies.versions(name),
    queryFn: () =>
      api.get<StrategySummaryOut[]>(`/strategies/by-name/${encodeURIComponent(name)}/versions`),
    enabled: name.length > 0,
    ...options,
  });
}

/** Validate a rule spec. A mutation because it is a POST with a body the
 *  form owns, and it must not be cached across edits. */
export function useValidateRuleSpec(
  options?: MutationOpts<RuleSpecValidation, JsonObject>,
): UseMutationResult<RuleSpecValidation, ApiError, JsonObject> {
  return useMutation({
    mutationFn: (spec) => api.post<RuleSpecValidation>('/strategies/validate', spec),
    ...options,
  });
}

export function useCreateStrategy(
  options?: MutationOpts<StrategyDetailOut, StrategyCreateRequest>,
): UseMutationResult<StrategyDetailOut, ApiError, StrategyCreateRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<StrategyDetailOut>('/strategies', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.strategies.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

/**
 * Bring one back out of the Deleted folder.
 *
 * The pair to the existing `useArchiveStrategy`, which is what "delete" now
 * means: the owner's model is delete → Deleted folder → delete again, and the
 * first step has to be reversible for the folder to be a safety net rather
 * than a waiting room.
 */
export function useRestoreStrategy(
  options?: MutationOpts<StrategyDetailOut, number>,
): UseMutationResult<StrategyDetailOut, ApiError, number> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post<StrategyDetailOut>(`/strategies/${id}/restore`, {}),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.strategies.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

/**
 * Remove a strategy for good. Only from the Deleted folder.
 *
 * The backend refuses this on a strategy that has not been archived first,
 * and refuses it — with a sentence, not a 500 — when a backtest run still
 * refers to the row. Both refusals arrive as an `ApiError` the caller shows.
 */
export function useDeleteStrategy(
  options?: MutationOpts<void, number>,
): UseMutationResult<void, ApiError, number> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete<void>(`/strategies/${id}`),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.strategies.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useArchiveStrategy(
  options?: MutationOpts<StrategyDetailOut, number>,
): UseMutationResult<StrategyDetailOut, ApiError, number> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (strategyId) => api.post<StrategyDetailOut>(`/strategies/${strategyId}/archive`),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.strategies.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

/**
 * Archive / restore ONE registered registry variant (Strategies tab, issue 4).
 *
 * The S2 family shares a single StrategyDef, so `useArchiveStrategy` (which
 * archives the DEF) would delete EVERY variant. These touch only the variant
 * row — the safe per-variant delete/restore the registry's multi-delete uses.
 */
export function useArchiveVariant(
  options?: MutationOpts<void, number>,
): UseMutationResult<void, ApiError, number> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variantId) => api.post<void>(`/strategies/variants/${variantId}/archive`),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.strategies.registry() });
      void client.invalidateQueries({ queryKey: qk.strategies.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useRestoreVariant(
  options?: MutationOpts<void, number>,
): UseMutationResult<void, ApiError, number> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variantId) => api.post<void>(`/strategies/variants/${variantId}/restore`),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.strategies.registry() });
      void client.invalidateQueries({ queryKey: qk.strategies.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

// ══════════════════════════════════════════════════════════════════════
//  Lifecycle
// ══════════════════════════════════════════════════════════════════════

export function useLifecycleCapabilities(
  options?: QueryOpts<LifecycleCapabilities>,
): UseQueryResult<LifecycleCapabilities, ApiError> {
  return useQuery({
    queryKey: qk.lifecycle.capabilities(),
    queryFn: () => api.get<LifecycleCapabilities>('/lifecycle/capabilities'),
    staleTime: Infinity,
    ...options,
  });
}

export function useLifecycleVersions(
  filters?: { stage?: LifecycleStage; strategy_name?: string },
  options?: QueryOpts<LifecycleVersionOut[]>,
): UseQueryResult<LifecycleVersionOut[], ApiError> {
  return useQuery({
    queryKey: qk.lifecycle.versions(filters?.stage, filters?.strategy_name),
    queryFn: () =>
      api.get<LifecycleVersionOut[]>('/lifecycle/versions', { query: asParams(filters) }),
    ...options,
  });
}

export function useLifecycleHistory(
  strategyDefId: number | null,
  options?: QueryOpts<LifecycleEventOut[]>,
): UseQueryResult<LifecycleEventOut[], ApiError> {
  return useQuery({
    queryKey: qk.lifecycle.history(strategyDefId ?? -1),
    queryFn: () => api.get<LifecycleEventOut[]>(`/lifecycle/${strategyDefId}/history`),
    enabled: strategyDefId !== null && strategyDefId > 0,
    ...options,
  });
}

/**
 * Evaluate the promotion gates.
 *
 * Never cache this beyond a few seconds. A stale green scorecard is how a
 * strategy gets armed against evidence that no longer holds.
 */
export function useGateScorecard(
  strategyDefId: number | null,
  params?: { target_stage?: LifecycleStage; backtest_run_id?: number; gate_set?: string },
  options?: QueryOpts<ScorecardOut>,
): UseQueryResult<ScorecardOut, ApiError> {
  return useQuery({
    queryKey: qk.lifecycle.gates(
      strategyDefId ?? -1,
      params?.target_stage,
      params?.backtest_run_id,
    ),
    queryFn: () =>
      api.get<ScorecardOut>(`/lifecycle/${strategyDefId}/gates`, { query: asParams(params) }),
    enabled: strategyDefId !== null && strategyDefId > 0,
    staleTime: 0,
    gcTime: 5_000,
    ...options,
  });
}

export function useRegisterLifecycle(
  options?: MutationOpts<JsonObject, LifecycleRegisterRequest>,
): UseMutationResult<JsonObject, ApiError, LifecycleRegisterRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<JsonObject>('/lifecycle/register', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.lifecycle.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function usePromoteStrategy(
  options?: MutationOpts<JsonObject, PromotionRequest>,
): UseMutationResult<JsonObject, ApiError, PromotionRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<JsonObject>('/lifecycle/promote', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.lifecycle.all });
      void client.invalidateQueries({ queryKey: qk.strategies.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useDemoteStrategy(
  options?: MutationOpts<JsonObject, PromotionRequest>,
): UseMutationResult<JsonObject, ApiError, PromotionRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<JsonObject>('/lifecycle/demote', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.lifecycle.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useRetireStrategy(
  options?: MutationOpts<JsonObject, PromotionRequest>,
): UseMutationResult<JsonObject, ApiError, PromotionRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<JsonObject>('/lifecycle/retire', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.lifecycle.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

/** Force a transition past a failing gate. Always audited, never quiet. */
export function useOverrideGate(
  options?: MutationOpts<JsonObject, LifecycleOverrideRequest>,
): UseMutationResult<JsonObject, ApiError, LifecycleOverrideRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<JsonObject>('/lifecycle/override', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.lifecycle.all });
      void client.invalidateQueries({ queryKey: ['system', 'audit'] });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}


// ══════════════════════════════════════════════════════════════════════
//  Portfolio
// ══════════════════════════════════════════════════════════════════════

export function usePortfolioSummary(
  options?: QueryOpts<PortfolioSummary>,
): UseQueryResult<PortfolioSummary, ApiError> {
  return useQuery({
    queryKey: qk.portfolio.summary(),
    queryFn: () => api.get<PortfolioSummary>('/portfolio/summary'),
    refetchInterval: POLL.safety,
    ...options,
  });
}

export function useBalances(
  deploymentId?: number,
  options?: QueryOpts<BalanceOut[]>,
): UseQueryResult<BalanceOut[], ApiError> {
  return useQuery({
    queryKey: qk.portfolio.balances(deploymentId),
    queryFn: () =>
      api.get<BalanceOut[]>('/portfolio/balances', { query: { deployment_id: deploymentId } }),
    refetchInterval: POLL.operational,
    ...options,
  });
}

export function useEquityCurve(
  deploymentId: number | null,
  params?: { start?: string; end?: string; max_points?: number },
  options?: QueryOpts<EquityPointOut[]>,
): UseQueryResult<EquityPointOut[], ApiError> {
  return useQuery({
    queryKey: qk.portfolio.equity(
      deploymentId ?? -1,
      params?.start,
      params?.end,
      params?.max_points,
    ),
    queryFn: () =>
      api.get<EquityPointOut[]>('/portfolio/equity', {
        query: { deployment_id: deploymentId, ...asParams(params) },
      }),
    enabled: deploymentId !== null && deploymentId > 0,
    ...options,
  });
}

export function useDailyPnL(
  deploymentId: number | null,
  params?: { start?: string; end?: string },
  options?: QueryOpts<DailyPnLOut[]>,
): UseQueryResult<DailyPnLOut[], ApiError> {
  return useQuery({
    queryKey: qk.portfolio.daily(deploymentId ?? -1, params?.start, params?.end),
    queryFn: () =>
      api.get<DailyPnLOut[]>('/portfolio/daily', {
        query: { deployment_id: deploymentId, ...asParams(params) },
      }),
    enabled: deploymentId !== null && deploymentId > 0,
    ...options,
  });
}

export function useTrades(
  query?: TradeQuery,
  options?: QueryOpts<Page<TradeOut>>,
): UseQueryResult<Page<TradeOut>, ApiError> {
  return useQuery({
    queryKey: qk.portfolio.trades(query),
    queryFn: () => api.get<Page<TradeOut>>('/portfolio/trades', { query: asParams(query) }),
    ...options,
  });
}

export interface PerformanceStatsQuery {
  deployment_id?: number;
  backtest_run_id?: number;
  symbol?: string;
  start?: string;
  end?: string;
}

export function usePerformanceStats(
  query?: PerformanceStatsQuery,
  options?: QueryOpts<PerformanceStatsOut>,
): UseQueryResult<PerformanceStatsOut, ApiError> {
  return useQuery({
    queryKey: qk.portfolio.stats(query as JsonObject | undefined),
    queryFn: () => api.get<PerformanceStatsOut>('/portfolio/stats', { query: asParams(query) }),
    ...options,
  });
}

export function useCorrelation(
  params?: { start?: string; end?: string; method?: 'pearson' | 'spearman' },
  options?: QueryOpts<CorrelationReport>,
): UseQueryResult<CorrelationReport, ApiError> {
  return useQuery({
    queryKey: qk.portfolio.correlation(params),
    queryFn: () =>
      api.get<CorrelationReport>('/portfolio/correlation', { query: asParams(params) }),
    ...options,
  });
}

export interface AttributionQuery {
  dimension?: AttributionDimension;
  deployment_id?: number;
  backtest_run_id?: number;
  start?: string;
  end?: string;
}

export function useAttribution(
  query?: AttributionQuery,
  options?: QueryOpts<AttributionReport>,
): UseQueryResult<AttributionReport, ApiError> {
  return useQuery({
    queryKey: qk.portfolio.attribution(query as JsonObject | undefined),
    queryFn: () =>
      api.get<AttributionReport>('/portfolio/attribution', { query: asParams(query) }),
    ...options,
  });
}

/** Risk and risk-adjusted stats, annualised on 365 — crypto does not close. */
export function usePerformanceReport(
  deploymentId: number | null,
  params?: { start?: string; end?: string },
  options?: QueryOpts<PerformanceReport>,
): UseQueryResult<PerformanceReport, ApiError> {
  return useQuery({
    queryKey: qk.portfolio.performance(deploymentId ?? -1, params?.start, params?.end),
    queryFn: () =>
      api.get<PerformanceReport>('/portfolio/performance', {
        query: { deployment_id: deploymentId, ...asParams(params) },
      }),
    enabled: deploymentId !== null && deploymentId > 0,
    retry: false,
    ...options,
  });
}

export interface ExcursionQuery {
  timeframe: Timeframe;
  deployment_id?: number;
  backtest_run_id?: number;
  symbol?: string;
  limit?: number;
  include_stop_sweep?: boolean;
  [key: string]: unknown;
}

/**
 * MAE/MFE per trade.
 *
 * Two fields in the response decide whether the result can be trusted:
 * `stop_censored_pct` (trades that exited at their stop cannot record MAE
 * above 1R, so fitting a stop to this only recovers the stop you already
 * chose) and `ambiguous_pct` (one bar contained both stop and target).
 * Render both prominently or do not render the chart.
 */
export function useExcursion(
  query: ExcursionQuery | null,
  options?: QueryOpts<ExcursionReport>,
): UseQueryResult<ExcursionReport, ApiError> {
  return useQuery({
    queryKey: qk.portfolio.excursion(query as JsonObject | undefined),
    queryFn: () =>
      api.get<ExcursionReport>('/portfolio/excursion', {
        query: asParams(query ?? undefined),
        timeoutMs: 120_000,
      }),
    enabled: query !== null,
    ...options,
  });
}

// ══════════════════════════════════════════════════════════════════════
//  Deployments
// ══════════════════════════════════════════════════════════════════════

export interface DeploymentQuery {
  status?: string;
  environment?: string;
  symbol?: string;
  include_closed?: boolean;
}

export function useDeployments(
  query?: DeploymentQuery,
  options?: QueryOpts<DeploymentOut[]>,
): UseQueryResult<DeploymentOut[], ApiError> {
  return useQuery({
    queryKey: qk.deployments.list(query as JsonObject | undefined),
    queryFn: () => api.get<DeploymentOut[]>('/deployments', { query: asParams(query) }),
    refetchInterval: POLL.operational,
    ...options,
  });
}

export function useDeployment(
  deploymentId: number | null,
  options?: QueryOpts<DeploymentOut>,
): UseQueryResult<DeploymentOut, ApiError> {
  return useQuery({
    queryKey: qk.deployments.detail(deploymentId ?? -1),
    queryFn: () => api.get<DeploymentOut>(`/deployments/${deploymentId}`),
    enabled: deploymentId !== null && deploymentId > 0,
    refetchInterval: POLL.operational,
    ...options,
  });
}

/** Answers "is it trading?", not "is the process up?". */
export function useDeploymentStatus(
  deploymentId: number | null,
  options?: QueryOpts<DeploymentStatusOut>,
): UseQueryResult<DeploymentStatusOut, ApiError> {
  return useQuery({
    queryKey: qk.deployments.status(deploymentId ?? -1),
    queryFn: () => api.get<DeploymentStatusOut>(`/deployments/${deploymentId}/status`),
    enabled: deploymentId !== null && deploymentId > 0,
    refetchInterval: POLL.safety,
    ...options,
  });
}

export function usePortfolioBookings(
  deploymentId?: number,
  options?: QueryOpts<PortfolioBookingOut[]>,
): UseQueryResult<PortfolioBookingOut[], ApiError> {
  return useQuery({
    queryKey: qk.portfolio.bookings(deploymentId),
    queryFn: () =>
      api.get<PortfolioBookingOut[]>('/portfolio/bookings', {
        query: { deployment_id: deploymentId },
      }),
    refetchInterval: POLL.operational,
    ...options,
  });
}

/** Separate-process control-plane health. This is intentionally not inferred
 * from API health or websocket state. [P4] */
export function useExecutorStatus(
  options?: QueryOpts<ExecutorStatusOut>,
): UseQueryResult<ExecutorStatusOut, ApiError> {
  return useQuery({
    queryKey: qk.commands.executor(),
    queryFn: () => api.get<ExecutorStatusOut>('/commands/executor-status'),
    refetchInterval: POLL.safety,
    ...options,
  });
}

export function useTradingCommand(
  options?: MutationOpts<TradingCommandAcceptedOut, TradingCommandRequest>,
): UseMutationResult<TradingCommandAcceptedOut, ApiError, TradingCommandRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post<TradingCommandAcceptedOut>('/commands', body, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.commands.all });
      void client.invalidateQueries({ queryKey: qk.deployments.all });
      void client.invalidateQueries({ queryKey: qk.portfolio.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useConfirmOneWayContract(
  options?: MutationOpts<DeploymentOut, { deploymentId: number; reason: string }>,
): UseMutationResult<DeploymentOut, ApiError, { deploymentId: number; reason: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ deploymentId, reason }) =>
      api.post<DeploymentOut>(`/deployments/${deploymentId}/confirm-one-way-contract`, {
        reason,
        confirmation: 'CONFIRM ONE-WAY BOTH',
      }),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.deployments.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useDeploymentLogs(
  deploymentId: number | null,
  limit = 200,
  options?: QueryOpts<LogLineOut[]>,
): UseQueryResult<LogLineOut[], ApiError> {
  return useQuery({
    queryKey: qk.deployments.logs(deploymentId ?? -1, limit),
    queryFn: () => api.get<LogLineOut[]>(`/deployments/${deploymentId}/logs`, { query: { limit } }),
    enabled: deploymentId !== null && deploymentId > 0,
    refetchInterval: POLL.operational,
    ...options,
  });
}

/**
 * One deployment's drift report, refreshed every 30 s (POLL.ambient: a verdict
 * that moves per closed trade). A body that is not the report's shape (an
 * available report without an object payload) is a `bad_response` error, never
 * typed data the panel would crash on [S4].
 */
export function useDeploymentDrift(
  deploymentId: number | null,
  options?: QueryOpts<DriftReportOut>,
): UseQueryResult<DriftReportOut, ApiError> {
  return useQuery({
    queryKey: qk.deployments.drift(deploymentId ?? -1),
    queryFn: () =>
      api
        .get<unknown>(`/deployments/${deploymentId}/drift`)
        .then(expectShape<DriftReportOut>(`/deployments/${deploymentId}/drift`, driftReportShape)),
    enabled: deploymentId !== null && deploymentId > 0,
    refetchInterval: POLL.ambient,
    ...options,
  });
}

export function useCreateDeployment(
  options?: MutationOpts<DeploymentOut, DeploymentCreateRequest>,
): UseMutationResult<DeploymentOut, ApiError, DeploymentCreateRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<DeploymentOut>('/deployments', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.deployments.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export interface DeploymentAction {
  deploymentId: number;
  body: DeploymentActionRequest;
}

/**
 * Start / pause / stop / re-arm.
 *
 * One hook, four verbs — the bodies would otherwise be identical. Named
 * `use…` so the rules-of-hooks lint understands it, and so the four thin
 * wrappers below are legitimate hooks rather than a lint suppression.
 */
export function useDeploymentAction(
  verb: 'start' | 'pause' | 'stop' | 'halt' | 'flatten-and-close' | 'rearm' | 'retire',
  options?: MutationOpts<DeploymentOut, DeploymentAction>,
): UseMutationResult<DeploymentOut, ApiError, DeploymentAction> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ deploymentId, body }) =>
      api.post<DeploymentOut>(`/deployments/${deploymentId}/${verb}`, body, {
        timeoutMs: 60_000,
      }),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.deployments.all });
      void client.invalidateQueries({ queryKey: qk.portfolio.all });
      void client.invalidateQueries({ queryKey: qk.system.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useStartDeployment(
  options?: MutationOpts<DeploymentOut, DeploymentAction>,
): UseMutationResult<DeploymentOut, ApiError, DeploymentAction> {
  return useDeploymentAction('start', options);
}

/** Close a never-started deployment that can no longer run (→ CLOSED, audited). */
export function useRetireDeployment(
  options?: MutationOpts<DeploymentOut, DeploymentAction>,
): UseMutationResult<DeploymentOut, ApiError, DeploymentAction> {
  return useDeploymentAction('retire', options);
}

/** Close ONE attributed lot (reduce-only) and drop its per-lot stop/target [R2.2].
 *  Sibling lots and the opposite leg are untouched. */
export interface CloseTradeAction {
  deploymentId: number;
  tradeId: string;
  body: { reason: string };
}
export function useCloseVirtualTrade(
  options?: MutationOpts<DeploymentOut, CloseTradeAction>,
): UseMutationResult<DeploymentOut, ApiError, CloseTradeAction> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ deploymentId, tradeId, body }) =>
      api.post<DeploymentOut>(
        `/deployments/${deploymentId}/trades/${tradeId}/close`,
        body,
        { timeoutMs: 60_000 },
      ),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.deployments.all });
      void client.invalidateQueries({ queryKey: qk.monitoring.all });
      void client.invalidateQueries({ queryKey: qk.portfolio.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

/**
 * The SERVER deploy contract, evaluated per market without creating anything —
 * the wizard shows this verdict, not a browser-assembled one, before Deploy.
 */
export function useDeployContractPreflight(
  options?: MutationOpts<DeployContractPreflightOut, DeploymentBasketCreateRequest>,
): UseMutationResult<DeployContractPreflightOut, ApiError, DeploymentBasketCreateRequest> {
  return useMutation({
    mutationFn: (body) =>
      api.post<DeployContractPreflightOut>('/deployments/preflight', body, { timeoutMs: 60_000 }),
    ...options,
  });
}

/** One deploy decision → one STOPPED deployment per market, sharing a basket_ref. */
export function useCreateDeploymentBasket(
  options?: MutationOpts<DeploymentBasketOut, DeploymentBasketCreateRequest>,
): UseMutationResult<DeploymentBasketOut, ApiError, DeploymentBasketCreateRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post<DeploymentBasketOut>('/deployments/basket', body, { timeoutMs: 60_000 }),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.deployments.all });
      void client.invalidateQueries({ queryKey: qk.monitoring.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function usePauseDeployment(
  options?: MutationOpts<DeploymentOut, DeploymentAction>,
): UseMutationResult<DeploymentOut, ApiError, DeploymentAction> {
  return useDeploymentAction('pause', options);
}

export function useStopDeployment(
  options?: MutationOpts<DeploymentOut, DeploymentAction>,
): UseMutationResult<DeploymentOut, ApiError, DeploymentAction> {
  return useDeploymentAction('stop', options);
}

export function useHaltDeployment(
  options?: MutationOpts<DeploymentOut, DeploymentAction>,
): UseMutationResult<DeploymentOut, ApiError, DeploymentAction> {
  return useDeploymentAction('halt', options);
}

export function useFlattenAndCloseDeployment(
  options?: MutationOpts<DeploymentOut, DeploymentAction>,
): UseMutationResult<DeploymentOut, ApiError, DeploymentAction> {
  return useDeploymentAction('flatten-and-close', options);
}

export function useRearmDeployment(
  options?: MutationOpts<DeploymentOut, DeploymentRearmAction>,
): UseMutationResult<DeploymentOut, ApiError, DeploymentRearmAction> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ deploymentId, body }) =>
      api.post<DeploymentOut>(`/deployments/${deploymentId}/rearm`, body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.deployments.all });
      void client.invalidateQueries({ queryKey: qk.system.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export interface DeploymentRearmAction {
  deploymentId: number;
  /** A strategy re-arm carries no account drawdown-baseline fields: it never
   *  touches the account emergency stop (the server refuses extra fields). */
  body: Pick<RearmRequest, 'reason' | 'confirmation'>;
}

export interface DeploymentTerminateAction {
  deploymentId: number;
  body: { reason: string; confirmation: string };
}

/** Irreversible teardown: flatten, prove flat, book, retire, close [§5.2]. The
 *  server validates the `TERMINATE {public_ref}` phrase and answers 409 on a
 *  mismatch, so a stray call cannot retire a live strategy. */
export function useTerminateDeployment(
  options?: MutationOpts<DeploymentOut, DeploymentTerminateAction>,
): UseMutationResult<DeploymentOut, ApiError, DeploymentTerminateAction> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ deploymentId, body }) =>
      api.post<DeploymentOut>(`/deployments/${deploymentId}/terminate`, body, {
        timeoutMs: 60_000,
      }),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.deployments.all });
      void client.invalidateQueries({ queryKey: qk.portfolio.all });
      void client.invalidateQueries({ queryKey: qk.monitoring.all });
      void client.invalidateQueries({ queryKey: qk.system.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

/**
 * The one Overview response (M2-S5): the fleet, the ribbon counts and the basket
 * card read this query, `rows` included, instead of polling the deployment rows
 * separately. A malformed answer is a failed read (`monitoringOverviewShape`):
 * the last good answer stays, dimmed, and nothing turns green on it [S4].
 */
export function useMonitoringOverview(
  includeClosed = false,
  options?: { refetchInterval?: number },
): UseQueryResult<MonitoringOverview, ApiError> {
  return useQuery<MonitoringOverview, ApiError>({
    queryKey: [...qk.monitoring.overview(), { includeClosed }] as const,
    queryFn: () =>
      api
        .get<unknown>('/monitoring/overview', {
          query: includeClosed ? { include_closed: true } : undefined,
        })
        .then(expectShape<MonitoringOverview>('/monitoring/overview', monitoringOverviewShape)),
    refetchInterval: options?.refetchInterval ?? POLL.liveMoney,
  });
}

export function useMonitoringGroup(deploymentId: number) {
  return useQuery({
    queryKey: [...qk.monitoring.all, 'group', deploymentId],
    queryFn: () => api.get<MonitoringGroupDetail>(`/monitoring/groups/${deploymentId}`),
    refetchInterval: POLL.liveMoney,
  });
}

export function useMonitoringGroupDecisions(deploymentId: number, hours = 48, enabled = true) {
  return useQuery({
    queryKey: [...qk.monitoring.all, 'group-decisions', deploymentId, hours] as const,
    queryFn: () => api.get<GroupDecisionsOut>(
      `/monitoring/groups/${deploymentId}/decisions`, { query: { hours } }),
    refetchInterval: POLL.liveMoney,
    enabled: enabled && deploymentId > 0,
  });
}

/**
 * The exchange-ledger deployment rows behind Monitoring, the Dashboard and the
 * top-bar protection alarm (one shared query per `includeClosed` key). The rows
 * are NOT environment-scoped: the route lists every deployment
 * (`list_all()`, routes_monitoring_workspace.py). Unprotected positions are
 * read only through `useProtectionTruth` (components/monitoring), which ages
 * the rows and fails closed on malformed ones [mv2-D2].
 */
export function useMonitoringDeployments(
  includeClosed = false,
): UseQueryResult<MonitoringDeploymentRow[], ApiError> {
  return useQuery<MonitoringDeploymentRow[], ApiError>({
    queryKey: [...qk.monitoring.deployments(), { includeClosed }] as const,
    // Both keys (open-only and include-closed) pass the same guard, so every
    // consumer of the protection rows fails closed on a malformed answer.
    queryFn: () =>
      api
        .get<unknown>('/monitoring/deployments', {
          query: includeClosed ? { include_closed: true } : undefined,
        })
        .then(expectShape<MonitoringDeploymentRow[]>('/monitoring/deployments', deploymentRowsShape)),
    refetchInterval: POLL.liveMoney,
  });
}

// The single-deployment monitoring dossier, including the live terminate/flatten
// `operation` progress (phase, state, command status, exact reason). Opens for a
// TERMINATED (CLOSED) deployment too [A][C1].
export function useMonitoringDeploymentDetail(deploymentId: number | null) {
  return useQuery({
    queryKey: [...qk.monitoring.deployments(), 'detail', deploymentId] as const,
    queryFn: () => api.get<MonitoringDeploymentDetail>(`/monitoring/deployments/${deploymentId}`),
    enabled: deploymentId !== null,
    // Poll fast WHILE a terminate/flatten is genuinely in flight so the progress
    // box steps in near-real-time (the operation finishes in ~5s), including the
    // command-issued-but-barrier-not-yet-written window; back off to the slow
    // operational cadence once terminal — or if a barrier is wedged ACTIVE past a
    // deadline (executor died mid-teardown) — so an open dossier is never a
    // permanent hot loop [C1 review #9/#11].
    refetchInterval: (query) => {
      const op = query.state.data?.operation;
      if (!op) return POLL.operational;
      const teardown = /terminate|flatten|square/i.test(op.kind ?? '');
      const terminal = op.state === 'released' || op.state === 'superseded'
        || op.command_status === 'succeeded' || op.command_status === 'failed_safe'
        || op.command_status === 'uncertain';
      const ageMs = op.installed_at_ms ? Date.now() - op.installed_at_ms : 0;
      return teardown && !terminal && ageMs < 180_000 ? 1_500 : POLL.operational;
    },
  });
}

export function useEngineCityTelemetry(
  options?: { refetchInterval?: number },
): UseQueryResult<EngineCityTelemetryOut, ApiError> {
  return useQuery<EngineCityTelemetryOut, ApiError>({
    queryKey: qk.monitoring.engineCity(),
    queryFn: () =>
      api
        .get<unknown>('/monitoring/engine-city')
        .then(expectShape<EngineCityTelemetryOut>('/monitoring/engine-city', engineCityTelemetryShape)),
    refetchInterval: options?.refetchInterval ?? 2_000,
  });
}

// The engine room's "can we trade?" verdict with the exact cause (418 ban / 429
// backoff / no executor / stale reconcile / break / unreadable egress /
// credential) — never a generic "blocked".
// Read only through useEntryGate (components/monitoring/useEntryGate.ts), which
// ages it and combines it with the kill switch [mv2-D2].
export function useEngineCitySafety(
  options?: QueryOpts<EngineCitySafetyOut>,
): UseQueryResult<EngineCitySafetyOut, ApiError> {
  return useQuery({
    queryKey: ['monitoring-workspace', 'engine-city', 'safety'] as const,
    queryFn: () =>
      api
        .get<unknown>('/monitoring/engine-city/safety')
        .then(expectShape<EngineCitySafetyOut>('/monitoring/engine-city/safety', safetyVerdictShape)),
    refetchInterval: 2_000,
    // Refetch at once on mount or return: a cached green verdict must not read
    // as current for the 15 s default before the first answer.
    staleTime: 0,
    ...options,
  });
}

// Package 14 — Engine City v2. The healthy summary aggregates at scale (a fixed
// number of backend GROUP BY queries), so it is cheap to poll.
export function useEngineCitySummary() {
  return useQuery({
    queryKey: ['monitoring-workspace', 'engine-city', 'summary'] as const,
    queryFn: () => api.get<EngineCitySummaryOut>('/monitoring/engine-city/summary'),
    refetchInterval: 2_000,
  });
}

// One cell per exchange account: equity, maintenance margin, the read-time
// margin ratio, the per-account lease and its fencing epoch, order windows,
// egress, bound deployments, and the SK1 gross-cap block.
export function useEngineCityAccounts() {
  return useQuery({
    queryKey: ['monitoring-workspace', 'engine-city', 'accounts'] as const,
    queryFn: () => api.get<EngineCityAccountsOut>('/monitoring/engine-city/accounts'),
    refetchInterval: 2_000,
  });
}

// Incident + recovery lineage, newest first and bounded by `limit` so retained
// history cannot grow the read.
export function useEngineCityIncidents(limit: number) {
  return useQuery({
    queryKey: ['monitoring-workspace', 'engine-city', 'incidents', limit] as const,
    queryFn: () =>
      api.get<EngineCityIncidentsOut>(`/monitoring/engine-city/incidents?limit=${limit}`),
    refetchInterval: 10_000,
  });
}

// The causal-animation evidence stream: poll after a monotonic cursor. The view
// animates only genuinely-new ids, never on a browser timer.
export function useEngineCityEvidence(afterMs: number) {
  return useQuery({
    queryKey: ['monitoring-workspace', 'engine-city', 'evidence', afterMs] as const,
    queryFn: () => api.get<EngineCityEvidenceOut>(
      `/monitoring/engine-city/evidence?after_ms=${afterMs}`),
    refetchInterval: 2_000,
  });
}

export function useMonitoringTrades(deploymentId?: number) {
  return useQuery({
    queryKey: qk.monitoring.trades(deploymentId),
    queryFn: () =>
      api.get<MonitoringTrade[]>(
        `/monitoring/trades${deploymentId === undefined ? '' : `?deployment_id=${deploymentId}`}`,
      ),
    refetchInterval: 5_000,
  });
}

export function useExchangeExecutions(deploymentId?: number) {
  return useQuery({
    queryKey: qk.monitoring.executions(deploymentId),
    queryFn: () => api.get<ExchangeExecutionsOut>(`/monitoring/executions${deploymentId === undefined ? '' : `?deployment_id=${deploymentId}`}`),
    refetchInterval: 3_000,
  });
}

/**
 * Exchange positions per account, coin and side: Reconciliation › Coin & side and
 * the Positions workspace share this one key, so opening both costs one poll.
 * A malformed answer is a failed read (`coinSideRowsShape`) [S4].
 */
export function useMonitoringCoinSide(): UseQueryResult<MonitoringCoinSide[], ApiError> {
  return useQuery<MonitoringCoinSide[], ApiError>({
    queryKey: qk.monitoring.coinSide(),
    queryFn: () =>
      api
        .get<unknown>('/monitoring/coin-side')
        .then(expectShape<MonitoringCoinSide[]>('/monitoring/coin-side', coinSideRowsShape)),
    refetchInterval: 5_000,
  });
}

export function useMonitoringLimits() {
  return useQuery({
    queryKey: qk.monitoring.limits(),
    queryFn: () => api.get<MonitoringLimit[]>('/monitoring/limits'),
    refetchInterval: 5_000,
  });
}

/**
 * §5. Attach the deployment record (backtested flag, justifying run, spec
 * snapshot, gate scorecard) to a freshly created deployment.
 *
 * A separate write from the create so a create against a backend that predates
 * §7-C still succeeds. On a 404 the mutation resolves to `null` — the
 * deployment was created, but the record endpoint is not built yet, so the
 * caller reports the record as pending rather than treating the deploy as
 * failed.
 */
export interface DeploymentRecordWriteVars {
  deploymentId: number;
  body: DeploymentRecordWrite;
}

export function useWriteDeploymentRecord(
  options?: MutationOpts<DeploymentOut | null, DeploymentRecordWriteVars>,
): UseMutationResult<DeploymentOut | null, ApiError, DeploymentRecordWriteVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ deploymentId, body }) => {
      try {
        return await api.post<DeploymentOut>(`/deployments/${deploymentId}/record`, body);
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.code === 'not_found')) {
          return null;
        }
        throw error;
      }
    },
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.deployments.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

// ══════════════════════════════════════════════════════════════════════
//  Strategies redesign & live monitoring — §7 net-new surfaces
//
//  Each of these reads an endpoint the futures executor will provide. They use
//  `getOrNull`, so `data === null` is "awaiting futures executor" (render the
//  pending state) and any real failure still surfaces as an error. Nothing
//  here fabricates a value.
// ══════════════════════════════════════════════════════════════════════

/**
 * §7-A. The per-version registry aggregate.
 *
 * One row per strategy version with the money headline and the Backtested?
 * flag. Null until `/api/strategies/registry` exists — the page then falls
 * back to a client-join of `/backtest/runs`, which every version can answer.
 */
export function useStrategyRegistry(
  options?: QueryOpts<StrategyRegistryOut | null>,
): UseQueryResult<StrategyRegistryOut | null, ApiError> {
  return useQuery({
    queryKey: qk.strategies.registry(),
    queryFn: async () => {
      const out = await getOrNull<StrategyRegistryOut>('/strategies/registry');
      if (out === null) return null;
      // Accept ONLY the §7-A shape ({as_of, stale, rows}). If some other 200
      // body ever answers this path (a Control-Center deployment tree once
      // collided here and served {as_of, deployment_count, families}), treat it
      // as "aggregate not built yet" → client-join fallback, and never hand the
      // registry table a `rows` that isn't there (its `.map` blanked the whole
      // tab). See backend projections/registry.py.
      const rows: unknown = (out as { rows?: unknown }).rows;
      return Array.isArray(rows) ? out : null;
    },
    staleTime: 30_000,
    refetchInterval: 3_000,
    ...options,
  });
}

/**
 * §7-F/G/H/I. The consolidated per-(coin,side) block, the shared liquidation
 * and the account gross-vs-cap gauge.
 *
 * Polls on the safety interval — this is the "how close to liquidation"
 * surface. Null until `/api/portfolio/consolidated` exists.
 */
export function useConsolidatedPositions(
  options?: QueryOpts<ConsolidatedPositionsOut | null>,
): UseQueryResult<ConsolidatedPositionsOut | null, ApiError> {
  return useQuery({
    queryKey: qk.portfolio.consolidated(),
    queryFn: () => getOrNull<ConsolidatedPositionsOut>('/portfolio/consolidated'),
    refetchInterval: POLL.safety,
    ...options,
  });
}

/**
 * §7-J. The exchange-vs-app reconciliation status.
 *
 * A divergence is an alarm (S1); the deploy card refuses to arm while this is
 * stale or in drift. The real Phase-4 projection is `/api/reconciliation`;
 * an unavailable route is an error, not a nullable "not built yet" success.
 */
export function useReconciliation(
  options?: QueryOpts<ControlReconciliationOut>,
): UseQueryResult<ControlReconciliationOut, ApiError> {
  return useQuery({
    queryKey: qk.portfolio.reconciliation(),
    queryFn: ({ signal }) =>
      api
        .get<unknown>('/reconciliation', { signal })
        .then(expectShape<ControlReconciliationOut>('/reconciliation', reconciliationShape)),
    retry: false,
    refetchInterval: POLL.safety,
    ...options,
  });
}

/**
 * §7-K. The resting stop/target order detail for one lot.
 *
 * Null until `/api/portfolio/positions/{id}/orders` exists — the open-lots
 * table then shows the take-profit order id and each protective order's live
 * on-exchange state rather than only the stop price.
 */
export function usePositionOrders(
  positionId: string | null,
  options?: QueryOpts<PositionOrdersOut | null>,
): UseQueryResult<PositionOrdersOut | null, ApiError> {
  return useQuery({
    queryKey: qk.portfolio.positionOrders(positionId ?? ''),
    queryFn: () =>
      getOrNull<PositionOrdersOut>(
        `/portfolio/positions/${encodeURIComponent(positionId ?? '')}/orders`,
      ),
    enabled: positionId !== null && positionId.length > 0,
    refetchInterval: POLL.safety,
    ...options,
  });
}

/**
 * §7-E. The deploy preflight — the gate scorecard composed with the
 * exchange-facing gates (reconcile, venue filters + hedge mode + key scope,
 * shared liquidation + gross cap).
 *
 * A mutation, not a query: it is evaluated against a specific sized book at the
 * moment the wizard runs it, and re-running it is a deliberate act. Resolves to
 * `null` on a 404 so the wizard can fall back to the plain `/gates` scorecard
 * plus the frontend-evaluated gates and mark the exchange gates pending.
 */
export function useDeployPreflight(
  strategyDefId: number | null,
  options?: MutationOpts<PreflightOut | null, PreflightRequest>,
): UseMutationResult<PreflightOut | null, ApiError, PreflightRequest> {
  return useMutation({
    mutationFn: async (body) => {
      if (strategyDefId === null || strategyDefId <= 0) return null;
      try {
        return await api.post<PreflightOut>(`/lifecycle/${strategyDefId}/preflight`, body, {
          timeoutMs: 60_000,
        });
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.code === 'not_found')) {
          return null;
        }
        throw error;
      }
    },
    ...options,
  });
}

// ══════════════════════════════════════════════════════════════════════
//  Alerts
// ══════════════════════════════════════════════════════════════════════

export function useAlerts(
  query?: AlertQuery,
  options?: QueryOpts<Page<AlertOut>>,
): UseQueryResult<Page<AlertOut>, ApiError> {
  return useQuery({
    queryKey: qk.alerts.list(query),
    queryFn: () => api.get<Page<AlertOut>>('/alerts', { query: asParams(query) }),
    refetchInterval: POLL.operational,
    ...options,
  });
}

export function useAlert(
  alertId: number | null,
  options?: QueryOpts<AlertOut>,
): UseQueryResult<AlertOut, ApiError> {
  return useQuery({
    queryKey: qk.alerts.detail(alertId ?? -1),
    queryFn: () => api.get<AlertOut>(`/alerts/${alertId}`),
    enabled: alertId !== null && alertId > 0,
    ...options,
  });
}

export function useUnacknowledgedCount(
  minSeverity: AlertSeverity = 'warning',
  options?: QueryOpts<UnacknowledgedCountOut>,
): UseQueryResult<UnacknowledgedCountOut, ApiError> {
  return useQuery({
    queryKey: qk.alerts.unackCount(minSeverity),
    queryFn: () =>
      api
        .get<unknown>('/alerts/unacknowledged/count', {
          query: { min_severity: minSeverity },
        })
        .then(expectShape<UnacknowledgedCountOut>('/alerts/unacknowledged/count', unacknowledgedCountShape)),
    refetchInterval: POLL.safety,
    ...options,
  });
}

export function useUndeliveredAlerts(
  limit = 50,
  options?: QueryOpts<AlertOut[]>,
): UseQueryResult<AlertOut[], ApiError> {
  return useQuery({
    queryKey: qk.alerts.undelivered(limit),
    queryFn: () => api.get<AlertOut[]>('/alerts/undelivered', { query: { limit } }),
    ...options,
  });
}

export function useAcknowledgeAlerts(
  options?: MutationOpts<AlertOut[], { alert_ids: number[]; by?: string }>,
): UseMutationResult<AlertOut[], ApiError, { alert_ids: number[]; by?: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<AlertOut[]>('/alerts/ack', body),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.alerts.all });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useAlertConfig(
  options?: QueryOpts<AlertConfigOut>,
): UseQueryResult<AlertConfigOut, ApiError> {
  return useQuery({
    queryKey: qk.alerts.config(),
    queryFn: () => api.get<AlertConfigOut>('/alerts/config/channels'),
    ...options,
  });
}

/** Takes effect immediately and does NOT survive a restart — routing lives
 *  in .env. Say so on screen; a safety setting that only pretends to
 *  persist is worse than one that refuses to change. */
export function useSetAlertRouting(
  options?: MutationOpts<AlertConfigOut, Record<string, string[]>>,
): UseMutationResult<AlertConfigOut, ApiError, Record<string, string[]>> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (routes) => api.put<AlertConfigOut>('/alerts/config/routing', routes),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.alerts.config() });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useSendTestAlert(
  options?: MutationOpts<JsonObject, { channel?: string | null }>,
): UseMutationResult<JsonObject, ApiError, { channel?: string | null }> {
  return useMutation({
    mutationFn: (body) => api.post<JsonObject>('/alerts/test', body, { timeoutMs: 30_000 }),
    ...options,
  });
}

// ══════════════════════════════════════════════════════════════════════
//  Drawings
// ══════════════════════════════════════════════════════════════════════

export function useChartsWithDrawings(
  options?: QueryOpts<DrawingChartRef[]>,
): UseQueryResult<DrawingChartRef[], ApiError> {
  return useQuery({
    queryKey: qk.drawings.charts(),
    queryFn: () => api.get<DrawingChartRef[]>('/drawings/charts'),
    ...options,
  });
}

/**
 * Every drawing saved for an instrument — at every timeframe.
 *
 * The query key is the **symbol alone**, deliberately. A drawing is
 * anchored in (UTC timestamp, price), so it is defined at every interval;
 * asking the server again because the user moved from 1h to 15m would
 * refetch a set that cannot have changed, and would flip `isLoading`, which
 * is how the chart used to lose its overlays on a timeframe click (M7).
 *
 * Visibility is applied in the client from `visible_timeframes` — see
 * `DrawingToolbar`. The endpoint accepts `?timeframe=` for callers that
 * want the filter server-side; the chart does not use it.
 */
export function useDrawings(
  symbol: string,
  options?: QueryOpts<DrawingOut[]>,
): UseQueryResult<DrawingOut[], ApiError> {
  return useQuery({
    queryKey: qk.drawings.forSymbol(symbol),
    queryFn: () => api.get<DrawingOut[]>(`/drawings/${encodeURIComponent(symbol)}`),
    enabled: symbol.length > 0,
    staleTime: 30_000,
    ...options,
  });
}

export interface DrawingMutation {
  symbol: string;
  /** The timeframe the chart is on. Recorded as provenance on new rows. */
  drawnOn: Timeframe;
  drawing: DrawingIn;
}

export function useUpsertDrawing(
  options?: MutationOpts<DrawingOut, DrawingMutation>,
): UseMutationResult<DrawingOut, ApiError, DrawingMutation> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ symbol, drawnOn, drawing }) =>
      api.put<DrawingOut>(`/drawings/${encodeURIComponent(symbol)}`, drawing, {
        query: { drawn_on: drawnOn },
      }),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.drawings.forSymbol(vars.symbol) });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export interface DrawingBulkMutation {
  symbol: string;
  drawnOn: Timeframe;
  drawings: DrawingIn[];
}

/** All-or-nothing. A partial save leaving half a drawing set behind is
 *  worse than a failed one. */
export function useUpsertDrawings(
  options?: MutationOpts<DrawingOut[], DrawingBulkMutation>,
): UseMutationResult<DrawingOut[], ApiError, DrawingBulkMutation> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ symbol, drawnOn, drawings }) =>
      api.put<DrawingOut[]>(`/drawings/${encodeURIComponent(symbol)}/bulk`, drawings, {
        query: { drawn_on: drawnOn },
      }),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.drawings.forSymbol(vars.symbol) });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export interface DrawingVisibility {
  symbol: string;
  overlayId: string;
  /** `null` — the default for every new drawing — means every timeframe. */
  visibleTimeframes: Timeframe[] | null;
}

/**
 * Confine a drawing to some timeframes, or `null` to show it on all of them.
 *
 * A restriction, never an enablement: a level is a fact about the
 * instrument, so hiding it is the thing that has to be asked for.
 */
export function useSetDrawingVisibility(
  options?: MutationOpts<DrawingOut, DrawingVisibility>,
): UseMutationResult<DrawingOut, ApiError, DrawingVisibility> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ symbol, overlayId, visibleTimeframes }) =>
      api.patch<DrawingOut>(
        `/drawings/${encodeURIComponent(symbol)}/${encodeURIComponent(overlayId)}/visibility`,
        { visible_timeframes: visibleTimeframes },
      ),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.drawings.forSymbol(vars.symbol) });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export interface DrawingDeletion {
  symbol: string;
  overlayId: string;
}

export function useDeleteDrawing(
  options?: MutationOpts<void, DrawingDeletion>,
): UseMutationResult<void, ApiError, DrawingDeletion> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ symbol, overlayId }) =>
      api.delete<void>(
        `/drawings/${encodeURIComponent(symbol)}/${encodeURIComponent(overlayId)}`,
      ),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.drawings.forSymbol(vars.symbol) });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export interface DrawingClear {
  symbol: string;
  includeLocked?: boolean;
}

export function useClearDrawings(
  options?: MutationOpts<JsonObject, DrawingClear>,
): UseMutationResult<JsonObject, ApiError, DrawingClear> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ symbol, includeLocked }) =>
      api.delete<JsonObject>(`/drawings/${encodeURIComponent(symbol)}`, {
        query: { include_locked: includeLocked },
      }),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.drawings.forSymbol(vars.symbol) });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

// ══════════════════════════════════════════════════════════════════════
//  Database — the browsable record
// ══════════════════════════════════════════════════════════════════════

/**
 * The whitelist, and everything derived from it.
 *
 * **No polling.** Database reads are none of `POLL.safety`, `operational` or
 * `ambient` — nothing here answers "am I exposed right now". The row counts
 * are a snapshot and the platform writes while the tab is open, which is why
 * the footer carries a "counts may be stale" slot instead of a refetch timer:
 * saying the number is a snapshot costs nothing, and re-reading 13 `count(*)`
 * every 30 seconds for a screen nobody is watching costs the event loop that
 * also owns the exchange websocket (P4).
 */
export function useDbTables(
  options?: QueryOpts<DbTableListOut>,
): UseQueryResult<DbTableListOut, ApiError> {
  return useQuery({
    queryKey: qk.database.tables(),
    queryFn: () => api.get<DbTableListOut>('/db/tables'),
    staleTime: 30_000,
    ...options,
  });
}

/**
 * One page of one table.
 *
 * `placeholderData` is in the **predicate** form and scoped to the table,
 * which is the whole reason the endpoint echoes `table` in its body. The
 * unqualified `keepPreviousData` would leave table A's rows on screen under
 * table B's header for a frame — a believable lie, and exactly the defect
 * `Markets.tsx:184-186` guards against. Scoped, it keeps the smoothing where
 * it is safe (page, sort and filter changes WITHIN one table) and refuses it
 * where it is not.
 *
 * Note it is `placeholderData`, not `keepPreviousData: true` — that option
 * was removed in TanStack Query v5 and is silently ignored, so writing it
 * would look like a fix and do nothing.
 */
export function useDbRows(
  table: string | null,
  query: DbRowQuery,
  options?: QueryOpts<DbRowPageOut>,
): UseQueryResult<DbRowPageOut, ApiError> {
  return useQuery({
    queryKey: qk.database.rows(table ?? '', query),
    queryFn: () =>
      api.get<DbRowPageOut>(`/db/tables/${encodeURIComponent(table ?? '')}/rows`, {
        query: asParams(query),
      }),
    enabled: table !== null,
    placeholderData: (previous: DbRowPageOut | undefined) =>
      previous && previous.table === table ? previous : undefined,
    ...options,
  });
}

/**
 * One row in full, including the blob columns and the dependency analysis.
 *
 * Enabled only while the detail modal is open. There is deliberately no
 * separate delete-preflight endpoint: this response carries `deletable` and
 * `refusal_reason`, and every extra endpoint is attack surface.
 */
export function useDbRow(
  table: string | null,
  pk: string | null,
  options?: QueryOpts<DbRowDetailOut>,
): UseQueryResult<DbRowDetailOut, ApiError> {
  return useQuery({
    queryKey: qk.database.row(table ?? '', pk ?? ''),
    queryFn: () =>
      api.get<DbRowDetailOut>(
        `/db/tables/${encodeURIComponent(table ?? '')}/rows/${encodeURIComponent(pk ?? '')}`,
      ),
    enabled: table !== null && pk !== null,
    staleTime: 60_000,
    ...options,
  });
}

export interface DbRowDeletion {
  table: string;
  pk: string;
  /** Written to the audit log in the same transaction. Minimum 10 characters. */
  reason: string;
}

/**
 * Delete one row from an allowed table.
 *
 * **POST, not DELETE**, for two reasons that are both load-bearing.
 * `api.delete` accepts no body (`client.ts:270-271`) and the audited reason
 * must not travel in a query string; and safe methods are not origin-guarded
 * at all, so a `<img src="…/delete">` on any page the owner opens would fire
 * with no Origin header, no preflight and no interaction. As a POST it
 * inherits `_reject_cross_site` for free.
 *
 * Invalidation is narrow on purpose: this table's pages, and the table list
 * because the row count changed. Nothing broader.
 */
export function useDeleteDbRow(
  options?: MutationOpts<DbRowDeletedOut, DbRowDeletion>,
): UseMutationResult<DbRowDeletedOut, ApiError, DbRowDeletion> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ table, pk, reason }) =>
      api.post<DbRowDeletedOut>(
        `/db/tables/${encodeURIComponent(table)}/rows/${encodeURIComponent(pk)}/delete`,
        { reason },
      ),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void client.invalidateQueries({ queryKey: qk.database.rowsForTable(vars.table) });
      void client.invalidateQueries({ queryKey: qk.database.tables() });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

// ══════════════════════════════════════════════════════════════════════
//  Live plumbing
// ══════════════════════════════════════════════════════════════════════

/**
 * Connection state of the shared websocket.
 *
 * `stale` is the field the UI must respect: when true, every live number on
 * screen has to be visibly degraded. Displaying a frozen price as live is a
 * money-losing bug, not a cosmetic one.
 */
export function useConnection(): ConnectionState {
  return useSyncExternalStore(ws.subscribeState, ws.getState, ws.getState);
}

/**
 * Only the `stale` flag of the shared websocket. The snapshot is a boolean, so
 * the caller re-renders only when the stream flips between live and stale,
 * not on every status or heartbeat change (the Monitoring root reads this).
 */
export function useConnectionStale(): boolean {
  const stale = () => ws.getState().stale;
  return useSyncExternalStore(ws.subscribeState, stale, stale);
}

/**
 * Follow a background job to completion.
 *
 * Websocket-first with a polling fallback, so a dropped socket degrades to
 * a slower progress bar rather than one that silently stops at 4%.
 */
export function useJobProgress(jobId: string | null): {
  job: JobOut | null;
  isRunning: boolean;
  isDone: boolean;
  isFailed: boolean;
} {
  const [job, setJob] = useState<JobOut | null>(null);
  const jobIdRef = useRef(jobId);
  jobIdRef.current = jobId;

  useEffect(() => {
    setJob(null);
    if (jobId === null || jobId.length === 0) return;
    return subscribeJobs((incoming) => {
      if (incoming.job_id === jobIdRef.current) setJob(incoming);
    });
  }, [jobId]);

  const terminal = job !== null && ['completed', 'failed', 'cancelled'].includes(job.status);

  const polled = useQuery<JobOut | null, ApiError>({
    queryKey: ['job', jobId ?? ''],
    queryFn: async () => {
      const all = await api.get<JobOut[]>('/system/jobs', { query: { limit: 200 } });
      return all.find((candidate) => candidate.job_id === jobId) ?? null;
    },
    enabled: jobId !== null && jobId.length > 0 && !terminal,
    refetchInterval: 2_000,
  });

  const merged = useMemo(() => {
    if (job === null) return polled.data ?? null;
    if (!polled.data) return job;
    // Whichever snapshot is further along wins; the socket is usually ahead.
    return polled.data.progress > job.progress ? polled.data : job;
  }, [job, polled.data]);

  return {
    job: merged,
    isRunning:
      merged !== null && !['completed', 'failed', 'cancelled'].includes(merged.status),
    isDone: merged?.status === 'completed',
    isFailed: merged?.status === 'failed',
  };
}

/** A positions frame refreshes the position views at once, then at most every 5 s (M2-S5). */
export const LIVE_REFRESH_WINDOW_MS = 5_000;
/** ... and the account book (open P&L follows marks) at most every 10 s. */
export const LIVE_MONEY_REFRESH_WINDOW_MS = 10_000;

/** What a positions frame refreshes, and how often at most (`startLiveRefresh`). */
export const LIVE_REFRESH_PLAN: LiveRefreshPlan = {
  topic: WS_TOPIC_POSITIONS,
  windows: [
    {
      windowMs: LIVE_REFRESH_WINDOW_MS,
      keys: [
        ['portfolio', 'positions'],
        qk.portfolio.summary(),
        qk.monitoring.overview(),
        qk.monitoring.deployments(),
        qk.monitoring.coinSide(),
        [...qk.monitoring.all, 'group'],
      ],
    },
    { windowMs: LIVE_MONEY_REFRESH_WINDOW_MS, keys: [['portfolio-book', 'overview']] },
  ],
};

/**
 * Refresh safety-critical caches whenever the socket reports a change.
 *
 * Mount once, in the app shell. Positions, alerts and deployment state are
 * the three things that must never be stale on screen.
 *
 * Positions frames arrive up to every 2 s, and each used to invalidate the
 * heaviest Monitoring queries with no throttle (30 refetches a minute of the
 * Overview). They are now coalesced (M2-S5): the first frame refreshes at once,
 * then at most one refresh per 5 s window for the position views and one per
 * 10 s for the account book, and the last frame of a burst is never dropped.
 * Every coalesced invalidation keeps a fetch already in flight
 * (`cancelRefetch: false`) instead of aborting and restarting it, so an answer
 * slower than the window still lands [RV-9]. Only mounted queries refetch.
 */
export function useLiveCacheSync(): void {
  const client = useQueryClient();

  // One bridge per client; unmounting unsubscribes and cancels pending refreshes.
  useEffect(() => startLiveRefresh(client, ws, LIVE_REFRESH_PLAN), [client]);

  const invalidateAlerts = useCallback(() => {
    void client.invalidateQueries({ queryKey: qk.alerts.all });
  }, [client]);

  const invalidateDeployments = useCallback(() => {
    void client.invalidateQueries({ queryKey: qk.deployments.all });
    void client.invalidateQueries({ queryKey: qk.system.killSwitch() });
  }, [client]);

  useEffect(() => ws.subscribe('alerts', invalidateAlerts), [invalidateAlerts]);
  useEffect(() => ws.subscribe('deployments', invalidateDeployments), [invalidateDeployments]);
}


// ══════════════════════════════════════════════════════════════════════
//  Portfolio v2 — the exchange-ledger book. Spec: portfolio-dashboard-v2.md
// ══════════════════════════════════════════════════════════════════════

/** Environment query: omitted means "the running environment". */
function bookQuery(environment?: BookEnvironment, extra?: QueryParams): QueryParams | undefined {
  const query: QueryParams = { ...(extra ?? {}) };
  if (environment) query.environment = environment;
  return Object.keys(query).length > 0 ? query : undefined;
}

/**
 * Headline book: equity, P&L, today, strategies, trade statistics. Portfolio,
 * the Dashboard and the Monitoring Overview ribbon (M2-S6) share this key, so
 * they print the same cached answer. A malformed answer is a failed read on
 * all of them (`bookOverviewShape`); `'0E-12'` money is a valid zero [RV-1].
 */
export function useBook(
  environment?: BookEnvironment,
  options?: { refetchInterval?: number },
): UseQueryResult<BookOverview, ApiError> {
  return useQuery<BookOverview, ApiError>({
    queryKey: ['portfolio-book', 'overview', environment ?? 'runtime'] as const,
    queryFn: ({ signal }) =>
      api
        .get<unknown>('/portfolio/book', { query: bookQuery(environment), signal })
        .then(expectShape<BookOverview>('/portfolio/book', bookOverviewShape)),
    refetchInterval: options?.refetchInterval ?? POLL.liveMoney,
  });
}

/** Daily / weekly / monthly realized P&L for the book, a strategy or a leg. */
export function useBookPeriods(
  granularity: BookGranularity,
  scope = 'book',
  options: { environment?: BookEnvironment; enabled?: boolean; startMs?: number } = {},
): UseQueryResult<BookPeriods, ApiError> {
  return useQuery<BookPeriods, ApiError>({
    queryKey: [
      'portfolio-book', 'periods', options.environment ?? 'runtime', granularity, scope,
      options.startMs ?? 'default',
    ] as const,
    queryFn: ({ signal }) =>
      api.get<BookPeriods>('/portfolio/book/periods', {
        query: bookQuery(options.environment, {
          granularity,
          scope,
          ...(options.startMs === undefined ? {} : { start_ms: options.startMs }),
        }),
        signal,
      }),
    enabled: options.enabled ?? true,
    refetchInterval: POLL.operational,
    // Scoped like `useDbRows`: keeping the previous buckets through a
    // granularity switch is safe ONLY because callers draw the response's own
    // `granularity` (daily bars under weekly labels was the defect this
    // replaced); another scope's or environment's buckets never stand in.
    placeholderData: (previous) =>
      previous
      && previous.scope === scope
      && (options.environment === undefined || previous.environment === options.environment)
        ? previous
        : undefined,
  });
}

/** Account equity, high-water mark and drawdown episodes (Drawdown tab). */
export function useBookEquity(options: { environment?: BookEnvironment; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['portfolio-book', 'equity', options.environment ?? 'runtime'] as const,
    queryFn: ({ signal }) =>
      api.get<BookEquity>('/portfolio/book/equity', { query: bookQuery(options.environment), signal }),
    enabled: options.enabled ?? true,
    // History-sized analytics that move only as equity changes: ambient cadence [P1].
    refetchInterval: POLL.ambient,
  });
}

/** Exit reasons, attribution and stop discipline of closed trades. */
export function useBookTrades(
  scope = 'book',
  options: { environment?: BookEnvironment; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['portfolio-book', 'trades', options.environment ?? 'runtime', scope] as const,
    queryFn: ({ signal }) =>
      api.get<BookTrades>('/portfolio/book/trades', {
        query: bookQuery(options.environment, { scope }),
        signal,
      }),
    enabled: options.enabled ?? true,
    // Closed-trade analytics change only when a trade closes: ambient cadence [P1].
    refetchInterval: POLL.ambient,
  });
}

/** MAE/MFE, stop sweep and edge ratio of closed trades (Stops tab, heavier). */
export function useBookExcursion(options: { environment?: BookEnvironment; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['portfolio-book', 'excursion', options.environment ?? 'runtime'] as const,
    queryFn: ({ signal }) =>
      api.get<BookExcursion>('/portfolio/book/excursion', { query: bookQuery(options.environment), signal }),
    enabled: options.enabled ?? true,
    refetchInterval: POLL.ambient,
  });
}

/** Futures candle freshness and the last nightly top-up result (Dashboard). */
export function useMarketDataStatus() {
  return useQuery({
    queryKey: ['system', 'market-data-status'] as const,
    queryFn: ({ signal }) => api.get<MarketDataStatus>('/system/market-data-status', { signal }),
    refetchInterval: POLL.ambient,
  });
}
